import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

// scrollTo is defined inside the component to access manualScrollRef

const Note = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  return (
    <div className="my-[16px] p-[12px_16px] rounded-[6px] bg-[#F5F5F6] border border-[#E5E5ED] border-l-[3px] border-l-[#1D1D1E]">
      <p className="font-body text-[13px] text-[#4A4A4C] leading-relaxed">
        <span className="font-semibold text-base-black">{t('docs.notePrefix')}</span>
        {children}
      </p>
    </div>
  );
};

const Tip = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  return (
    <div className="my-[16px] p-[12px_16px] rounded-[6px] bg-[#f0fdf4] border border-[#bbf7d0] border-l-[3px] border-l-green-500">
      <p className="font-body text-[13px] text-[#14532d] leading-relaxed">
        <span className="font-semibold">{t('docs.tipPrefix')}</span>
        {children}
      </p>
    </div>
  );
};

const Step = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <div className="flex gap-[12px] mb-[16px]">
    <span className="flex-shrink-0 w-[28px] h-[28px] rounded-full bg-[#1D1D1E] text-white flex items-center justify-center text-[13px] font-bold">
      {n}
    </span>
    <div className="font-body text-[14px] text-[#374151] leading-relaxed pt-[3px]">
      {children}
    </div>
  </div>
);

const Code = ({ children }: { children?: React.ReactNode }) => (
  <code className="font-mono text-[12px] bg-[#F5F5F6] border border-[#E5E5ED] rounded-[4px] px-[6px] py-[1px] text-base-black">
    {children}
  </code>
);

const ProBadge = () => (
  <span className="inline-flex items-center gap-[4px] px-[8px] py-[2px] rounded-[5px] bg-[#1D1D1E] text-white font-body text-[10px] font-bold uppercase tracking-wider align-middle ml-[8px]">Pro</span>
);

const EnterpriseBadge = () => (
  <span className="inline-flex items-center gap-[4px] px-[8px] py-[2px] rounded-[5px] bg-[#7C3AED] text-white font-body text-[10px] font-bold uppercase tracking-wider align-middle ml-[8px]">Enterprise</span>
);

export const DocsPage = () => {
  const { t } = useTranslation();

  const SECTIONS = [
    { id: 'getting-started', label: t('docs.gettingStartedTitle') },
    { id: 'create-queue', label: t('docs.createReviewTitle') },
    { id: 'managing-queue', label: t('docs.managingQueueTitle') },
    { id: 'queue-settings', label: 'Queue Settings' },
    { id: 'feature-toggles', label: 'Feature Toggles' },
    { id: 'privacy-mode', label: 'Privacy Mode' },
    { id: 'event-links', label: t('docs.meetingLinksTitle') },
    { id: 'password-protection', label: t('docs.passwordProtectionTitle') },
    { id: 'invite-tracker', label: t('docs.inviteTrackerTitle') },
    { id: 'qr-join', label: t('docs.qrJoinTitle') },
    { id: 'joining-queue', label: t('docs.joiningReviewTitle') },
    { id: 'wait-estimates', label: 'Wait Time Estimates' },
    { id: 'leaving', label: t('docs.leavingTitle') },
    { id: 'event-status', label: t('docs.meetingStatusTitle') },
    { id: 'teams', label: t('docs.teamsTitle') },
    { id: 'team-queues', label: t('docs.teamReviewsTitle') },
    { id: 'multi-team', label: t('docs.multiTeamTitle') },
    { id: 'chat', label: t('docs.chatTitle') },
    { id: 'notifications', label: t('docs.notificationsTitle') },
    { id: 'co-admins', label: t('docs.coAdminsTitle') },
    { id: 'queue-templates', label: 'Queue Templates' },
    { id: 'company', label: 'Company & Enterprise' },
    { id: 'settings', label: t('docs.settingsTitle') },
    { id: 'security', label: t('docs.securityTitle') },
    { id: 'webhook-notifications', label: t('docs.webhookNotificationsTitle') },
    { id: 'saved-webhooks', label: t('docs.savedWebhooksTitle') }
  ];

  const initialHash = window.location.hash.replace('#', '') || 'getting-started';
  const [activeId, setActiveId] = React.useState(initialHash);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  // Scroll to hash on mount
  React.useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ block: 'start' });
      }, 100);
    }
  }, []);

  const manualScrollRef = React.useRef<string | null>(null);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      // Set active immediately and block scroll spy briefly
      setActiveId(id);
      manualScrollRef.current = id;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${id}`);
      setTimeout(() => {
        manualScrollRef.current = null;
      }, 800);
    }
  };

  // Scroll spy — track which section is in view

  React.useEffect(() => {
    const sectionEls = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];

    const handleScroll = () => {
      // If user just clicked a TOC item, honor that for a moment
      if (manualScrollRef.current) return;

      const viewportHeight = window.innerHeight;

      // Find the last section whose top is in the upper half of the viewport
      let current = SECTIONS[0].id;
      for (const el of sectionEls) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportHeight * 0.4) {
          current = el.id;
        }
      }
      setActiveId(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="max-w-[1000px] mx-auto py-[32px] md:py-[48px]">
      <div className="sticky top-[60px] md:top-[70px] z-20 bg-white dark:bg-[#121214] pb-[12px] md:pb-[16px] -mx-[16px] px-[16px] md:mx-0 md:px-0">
        <h1 className="font-display text-[28px] md:text-[36px] font-bold text-base-black tracking-[-0.5px] mb-[8px]">
          {t('docs.title')}
        </h1>
        <p className="font-body text-[16px] text-gray-900 mb-[12px] md:mb-[16px]">
          {t('docs.subtitle')}
        </p>

        {/* Mobile TOC toggle + dropdown */}
        <div className="md:hidden relative">
          <button
            type="button"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="h-[36px] px-[14px] rounded-[6px] bg-[#F5F5F6] border border-[#E5E5ED] font-body text-[13px] font-medium text-base-black flex items-center gap-[6px]"
          >
            {mobileNavOpen ? t('docs.hideToc') : t('docs.showToc')}
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`}>
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {mobileNavOpen && (
            <div className="absolute top-[calc(100%+6px)] left-0 right-0 bg-white dark:bg-[#1C1C1F] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[6px] max-h-[50vh] overflow-y-auto z-30 animate-fadeInMenu">
              <p className="font-mono text-[10px] tracking-[0.08em] uppercase text-[#818185] font-bold px-[14px] py-[6px]">
                {t('docs.contents')}
              </p>
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setActiveId(s.id);
                    setMobileNavOpen(false);
                    manualScrollRef.current = s.id;
                    window.history.replaceState(null, '', `#${s.id}`);
                    setTimeout(() => {
                      const el = document.getElementById(s.id);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setTimeout(() => { manualScrollRef.current = null; }, 800);
                    }, 50);
                  }}
                  className={`w-full text-left px-[14px] py-[8px] font-body text-[13px] transition-colors ${
                    activeId === s.id
                      ? 'text-base-black font-semibold bg-[#F5F5F6] dark:bg-[#28282B]'
                      : 'text-[#818185] hover:text-base-black hover:bg-[#F7F7F8] dark:hover:bg-[#28282B]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-[40px]">
        {/* Desktop Sidebar TOC */}
        <nav className="hidden md:block md:w-[200px] flex-shrink-0 md:sticky md:top-[190px] md:self-start md:max-h-[calc(100vh-210px)] md:overflow-y-auto">
          <p className="font-mono text-[10px] tracking-[0.08em] uppercase text-[#818185] font-bold mb-[12px]">
            {t('docs.contents')}
          </p>
          <div className="flex flex-col gap-[2px]">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setActiveId(s.id);
                  manualScrollRef.current = s.id;
                  window.history.replaceState(null, '', `#${s.id}`);
                  setTimeout(() => {
                    const el = document.getElementById(s.id);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    setTimeout(() => { manualScrollRef.current = null; }, 800);
                  }, 50);
                }}
                className={`text-left px-[10px] py-[6px] rounded-[6px] font-body text-[13px] transition-all border-r-[2px] ${
                  activeId === s.id
                    ? 'text-base-black font-semibold bg-[#F5F5F6] border-r-[#1D1D1E]'
                    : 'text-[#818185] hover:text-base-black hover:bg-[#F7F7F8] border-r-transparent'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <article className="flex-1 min-w-0">
          {/* Getting Started */}
          <section id="getting-started" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.gettingStartedTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.gettingStartedP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              {t('docs.gettingStartedP2')}
            </p>
          </section>

          {/* Creating a Review */}
          <section id="create-queue" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.createReviewTitle')}
            </h2>
            <Step n={1}>
              <Trans i18nKey="docs.createReviewStep1" components={{ strong: <strong /> }} />
            </Step>
            <Step n={2}>
              <Trans i18nKey="docs.createReviewStep2" components={{ strong: <strong /> }} />
            </Step>
            <Step n={3}>
              <Trans i18nKey="docs.createReviewStep3" components={{ strong: <strong /> }} />
            </Step>
            <Step n={4}>
              <Trans i18nKey="docs.createReviewStep4" components={{ strong: <strong /> }} />
            </Step>
            <Step n={5}>
              <Trans i18nKey="docs.createReviewStep5" components={{ strong: <strong /> }} />
            </Step>
            <Step n={6}>
              <Trans i18nKey="docs.createReviewStep6" components={{ strong: <strong /> }} />
            </Step>
            <Step n={7}>
              <Trans i18nKey="docs.createReviewStep7" components={{ strong: <strong /> }} />
            </Step>
            {/* Illustration — Create Queue modal */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px] bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-[20px] flex flex-col gap-[12px]">
                {/* Modal header */}
                <div className="flex items-center justify-between">
                  <span className="font-display text-[18px] font-bold text-base-black">Create Queue</span>
                  <span className="text-[#818185] text-[14px]">✕</span>
                </div>
                {/* Title input */}
                <div className="h-[44px] px-[16px] flex items-center bg-white border border-[#BFBFC0] rounded-[24px] shadow-[0_0_0_4px_rgba(29,29,30,0.12)]">
                  <span className="font-body text-[16px] text-base-black">Sprint Queue</span>
                  <span className="w-[1.5px] h-[16px] bg-base-black ml-[1px] inline-block" />
                </div>
                {/* Optional pills */}
                <div className="flex flex-col gap-[6px]">
                  <span className="self-start inline-flex items-center gap-[4px] h-[32px] px-[12px] rounded-[32px] bg-[#EFEFF1] text-[#818185] font-body text-[12px] font-semibold">+ Add event link</span>
                  <span className="self-start inline-flex items-center gap-[4px] h-[32px] px-[12px] rounded-[32px] bg-[#EFEFF1] text-[#818185] font-body text-[12px] font-semibold">+ Add password</span>
                </div>
                {/* Action buttons */}
                <div className="flex gap-[8px]">
                  <span className="flex-1 h-[44px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[14px] font-medium flex items-center justify-center">Create</span>
                  <span className="flex-1 h-[44px] rounded-[32px] border border-gray-500 bg-gray-400 font-body text-[14px] font-medium text-base-black flex items-center justify-center">Cancel</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.createReviewFormCaption')}</p>
            </div>
            <Note>
              {t('docs.createReviewNote')}
            </Note>
          </section>

          {/* Managing the Queue */}
          <section id="managing-queue" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.managingQueueTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.managingQueueP1')}
            </p>
            <div className="mb-[16px] p-[16px] rounded-[12px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col gap-[8px]">
                <div className="flex items-center gap-[8px]">
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center bg-green-500 text-green-700">{t('docs.statusLabelInMeeting')}</span>
                  <span className="font-body text-[13px] text-[#374151]">{t('docs.statusInMeeting')}</span>
                </div>
                <div className="flex items-center gap-[8px]">
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center bg-orange-400 text-orange-800">{t('docs.statusLabelNextUp')}</span>
                  <span className="font-body text-[13px] text-[#374151]">{t('docs.statusNextUp')}</span>
                </div>
                <div className="flex items-center gap-[8px]">
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center bg-yellow-400 text-yellow-700">{t('docs.statusLabelOnStandby')}</span>
                  <span className="font-body text-[13px] text-[#374151]">{t('docs.statusOnStandby')}</span>
                </div>
                <div className="flex items-center gap-[8px]">
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center bg-gray-300 text-gray-600">{t('docs.statusLabelWaiting')}</span>
                  <span className="font-body text-[13px] text-[#374151]">{t('docs.statusWaiting')}</span>
                </div>
              </div>
            </div>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.activateDesc" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.markDoneDesc" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.dragDesc" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              <Trans i18nKey="docs.removeDesc" components={{ strong: <strong /> }} />
            </p>
            {/* Illustration — Queue participant card with actions */}
            <div className="mt-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[360px] flex flex-col gap-[6px]">
                <div className="flex items-center gap-[8px] p-[10px_12px] rounded-[16px] border bg-white border-orange-400 shadow-[0_2px_10px_rgba(251,146,60,0.15)]">
                  <svg className="flex-shrink-0 text-gray-700" width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.5"/><circle cx="11" cy="3" r="1.5"/><circle cx="5" cy="8" r="1.5"/><circle cx="11" cy="8" r="1.5"/><circle cx="5" cy="13" r="1.5"/><circle cx="11" cy="13" r="1.5"/></svg>
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">AC</span>
                  <span className="flex-1 font-body text-[15px] font-semibold text-[#111827] truncate">Alex Chen</span>
                  <span className="font-mono text-[12px] text-gray-700 flex-shrink-0">#1</span>
                  <span className="text-[10px] font-semibold px-[6px] py-[2px] rounded-[4px] min-w-[55px] text-center bg-orange-400 text-orange-800 flex-shrink-0">Next Up</span>
                  <div className="flex items-center gap-[6px] flex-shrink-0">
                    <span className="text-[11px] font-medium px-[8px] py-[3px] rounded-[8px] bg-[#1D1D1E] text-white">Activate</span>
                    <span className="text-[11px] font-medium px-[8px] py-[3px] rounded-[8px] border border-red-500 text-red-500">Remove</span>
                  </div>
                </div>
                <div className="flex items-center gap-[8px] p-[10px_12px] rounded-[16px] border bg-white border-yellow-400 opacity-50">
                  <svg className="flex-shrink-0 text-gray-700" width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.5"/><circle cx="11" cy="3" r="1.5"/><circle cx="5" cy="8" r="1.5"/><circle cx="11" cy="8" r="1.5"/><circle cx="5" cy="13" r="1.5"/><circle cx="11" cy="13" r="1.5"/></svg>
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">JL</span>
                  <span className="flex-1 font-body text-[15px] font-semibold text-[#111827] truncate">Jordan Lee</span>
                  <span className="font-mono text-[12px] text-gray-700 flex-shrink-0">#2</span>
                  <span className="text-[10px] font-semibold px-[6px] py-[2px] rounded-[4px] min-w-[55px] text-center bg-yellow-400 text-yellow-700 flex-shrink-0">On Standby</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.managingQueueCaption')}</p>
            </div>

            {/* Completed Participants Management */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.completedManagementTitle')}<ProBadge />
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              {t('docs.completedManagementP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.completedManagementReinstate" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.completedManagementRemove" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.completedManagementReinstateAll" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.completedManagementClearAll" components={{ strong: <strong /> }} />
            </p>

            {/* Illustration — Completed section with actions */}
            <div className="p-[16px] rounded-[16px] bg-[#F7F7F8] dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)]">
              <div className="max-w-[400px] flex flex-col gap-[4px]">
                {/* Section header with pill buttons */}
                <div className="flex items-center justify-between mb-[4px] ml-[4px]">
                  <span className="font-body text-[12px] font-semibold text-[#818185] dark:text-[#6B6B6F] uppercase tracking-wider">Completed</span>
                  <div className="flex items-center gap-[6px]">
                    <span className="inline-flex items-center gap-[4px] h-[28px] px-[10px] rounded-[32px] border border-[#0ea5c9]/30 bg-[#F8FCFF] dark:bg-[#0ea5c9]/10 font-body text-[11px] font-medium text-[#0ea5c9]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
                      Reinstate all
                    </span>
                    <span className="inline-flex items-center gap-[4px] h-[28px] px-[10px] rounded-[32px] border border-[#D8210C]/30 bg-[#FEF2F2] dark:bg-[#D8210C]/10 font-body text-[11px] font-medium text-[#D8210C]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      Clear all
                    </span>
                  </div>
                </div>
                {/* Completed card with actions */}
                <div className="flex items-center gap-[12px] p-[10px_12px] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] bg-white dark:bg-[#28282B] opacity-70">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#BFBFC0] dark:bg-[#4A4A4D] text-white text-[12px] font-semibold flex-shrink-0">MP</span>
                  <span className="flex-1 font-body text-[15px] font-medium text-[#818185] dark:text-[#A0A0A3] truncate">Morgan Park</span>
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[65px] text-center bg-gray-400 text-gray-700 dark:bg-[#333336] dark:text-[#6B6B6F]">Done</span>
                  <div className="flex items-center gap-[4px] flex-shrink-0">
                    <span className="flex items-center justify-center w-[28px] h-[28px] rounded-[8px] text-[#0ea5c9]" title="Reinstate">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
                    </span>
                    <span className="flex items-center justify-center w-[28px] h-[28px] rounded-[8px] text-[#D8210C]" title="Remove">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </span>
                  </div>
                </div>
                {/* Second completed card */}
                <div className="flex items-center gap-[12px] p-[10px_12px] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] bg-white dark:bg-[#28282B] opacity-70">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#BFBFC0] dark:bg-[#4A4A4D] text-white text-[12px] font-semibold flex-shrink-0">SK</span>
                  <span className="flex-1 font-body text-[15px] font-medium text-[#818185] dark:text-[#A0A0A3] truncate">Sam Kim</span>
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[65px] text-center bg-gray-400 text-gray-700 dark:bg-[#333336] dark:text-[#6B6B6F]">Done</span>
                  <div className="flex items-center gap-[4px] flex-shrink-0">
                    <span className="flex items-center justify-center w-[28px] h-[28px] rounded-[8px] text-[#818185] dark:text-[#6B6B6F]">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
                    </span>
                    <span className="flex items-center justify-center w-[28px] h-[28px] rounded-[8px] text-[#818185] dark:text-[#6B6B6F]">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] italic mt-[10px]">{t('docs.completedManagementCaption')}</p>
            </div>
          </section>

          {/* Queue Settings */}
          <section id="queue-settings" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              Queue Settings
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Queue admins can access all queue settings from the <strong>gear icon</strong> in the queue admin header. This opens a modal with a sidebar navigation containing tabs for Features, Notifications, Admins, and Templates (Enterprise only).
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              On mobile, the sidebar collapses to a horizontal icon row. The modal maintains a fixed size so switching between tabs is seamless.
            </p>

            {/* Illustration — Queue admin header with Settings + View as Participant pills */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[400px] flex flex-col gap-[12px]">
                {/* Admin header row */}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-[4px] font-body text-[13px] font-medium text-[#818185]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                    Dashboard
                  </span>
                  <div className="flex items-center gap-[6px]">
                    <span className="inline-flex items-center justify-center w-[28px] h-[28px] rounded-full border border-[#E5E5ED] bg-[#EFEFF1] text-[#818185]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    </span>
                    <span className="inline-flex items-center gap-[5px] h-[28px] px-[12px] rounded-[32px] border border-[#E5E5ED] bg-[#EFEFF1] font-body text-[11px] font-medium text-[#818185]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      View as Participant
                    </span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">The gear icon and View as Participant pill in the queue admin header.</p>
            </div>

            {/* Illustration — Queue Settings modal */}
            <div className="p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[420px] bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden">
                {/* Modal header */}
                <div className="flex items-center justify-between px-[20px] pt-[16px] pb-[10px] border-b border-[rgba(0,0,0,0.06)]">
                  <span className="font-display text-[16px] font-bold text-base-black">Queue Settings</span>
                  <span className="text-[#818185] text-[14px]">✕</span>
                </div>
                {/* Sidebar + content */}
                <div className="flex" style={{ height: 180 }}>
                  <div className="w-[140px] border-r border-[rgba(0,0,0,0.06)] flex flex-col py-[6px]">
                    <span className="flex items-center gap-[8px] px-[14px] py-[9px] bg-[#F0F0F2] font-body text-[12px] font-medium text-base-black">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7" ry="7"/><circle cx="16" cy="12" r="3"/></svg>
                      Features
                    </span>
                    <span className="flex items-center gap-[8px] px-[14px] py-[9px] font-body text-[12px] font-medium text-[#818185]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                      Notifications
                    </span>
                    <span className="flex items-center gap-[8px] px-[14px] py-[9px] font-body text-[12px] font-medium text-[#818185]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                      Admins
                    </span>
                    <span className="flex items-center gap-[8px] px-[14px] py-[9px] font-body text-[12px] font-medium text-[#818185]">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                      Template
                    </span>
                  </div>
                  {/* Features content */}
                  <div className="flex-1 p-[16px]">
                    <p className="font-body text-[10px] text-[#818185] mb-[12px]">Enable or disable features for this queue.</p>
                    <div className="flex flex-col gap-[8px]">
                      <div className="flex items-center justify-between">
                        <span className="font-body text-[12px] text-base-black">Queue Chat</span>
                        <span className="w-[32px] h-[18px] rounded-full bg-[#0ea5c9] relative"><span className="absolute top-[2px] left-[16px] w-[14px] h-[14px] rounded-full bg-white shadow" /></span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-body text-[12px] text-base-black">Queue Passwords</span>
                        <span className="w-[32px] h-[18px] rounded-full bg-[#0ea5c9] relative"><span className="absolute top-[2px] left-[16px] w-[14px] h-[14px] rounded-full bg-white shadow" /></span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-body text-[12px] text-base-black">QR Check-in</span>
                        <span className="w-[32px] h-[18px] rounded-full bg-[#BFBFC0] relative"><span className="absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-white shadow" /></span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-body text-[12px] text-base-black">Wait Time Estimates</span>
                        <span className="w-[32px] h-[18px] rounded-full bg-[#0ea5c9] relative"><span className="absolute top-[2px] left-[16px] w-[14px] h-[14px] rounded-full bg-white shadow" /></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">The Queue Settings modal with sidebar navigation. The Features tab shows toggle switches for each feature. Enterprise queues show additional toggles like QR Check-in and Wait Estimates.</p>
            </div>
          </section>

          {/* Feature Toggles */}
          <section id="feature-toggles" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              Feature Toggles
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Queue admins can enable or disable features for individual queues from the <strong>Features</strong> tab in Queue Settings. Changes take effect immediately via WebSocket, no refresh needed.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              All queues support these toggles:
            </p>
            <ul className="list-disc list-inside font-body text-[14px] text-[#374151] mb-[8px] space-y-[4px]">
              <li><strong>Queue Chat</strong>: enable or disable the in-queue chat sidebar for participants</li>
              <li><strong>Queue Passwords</strong>: enable or disable the ability to set a password on the queue</li>
            </ul>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Pro and Enterprise queues have additional toggles:
            </p>
            <ul className="list-disc list-inside font-body text-[14px] text-[#374151] mb-[16px] space-y-[4px]">
              <li><strong>Privacy Mode</strong>: hide participant identities from each other in the queue<ProBadge /><EnterpriseBadge /></li>
            </ul>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              Enterprise company queues have additional toggles:
            </p>
            <ul className="list-disc list-inside font-body text-[14px] text-[#374151] mb-[16px] space-y-[4px]">
              <li><strong>QR Check-in</strong>: generate per-participant QR codes for check-in at events<EnterpriseBadge /></li>
              <li><strong>Wait Time Estimates</strong>: show live countdown timers on participant cards<EnterpriseBadge /></li>
            </ul>

            {/* Privacy Mode illustration */}
            <h3 id="privacy-mode" className="font-display text-[18px] font-bold text-base-black mb-[8px] mt-[8px] scroll-mt-[230px] md:scroll-mt-[210px]">
              Privacy Mode<ProBadge /><EnterpriseBadge />
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              When Privacy Mode is on, participants can only see their own card clearly. Every other participant appears as an anonymous entry: no name, no initials. Status badges and position numbers remain visible so everyone still knows their place in the queue.
            </p>

            {/* Illustration */}
            <div className="my-[16px] p-[20px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col gap-[8px] max-w-[400px]">

                {/* Your card — clear */}
                <div className="flex items-center gap-[10px] p-[10px_14px] rounded-[16px] border border-[#BFBFC0] bg-[#F5F5F6] shadow-[0_2px_10px_rgba(42,60,68,0.08)]">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[11px] font-bold flex-shrink-0">
                    JW
                  </span>
                  <div className="flex-1 flex items-center gap-[6px] min-w-0">
                    <span className="font-body text-[14px] font-semibold text-[#111827]">James Wilson</span>
                    <span className="text-[10px] font-medium text-[#4A4A4C] bg-[#EFEFF1] px-[6px] py-[1px] rounded-[4px] flex-shrink-0">You</span>
                  </div>
                  <span className="flex-shrink-0 text-[10px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-orange-400 text-orange-900">Next Up</span>
                </div>

                {/* Obfuscated card 1 */}
                <div className="flex items-center gap-[10px] p-[10px_14px] rounded-[16px] border border-[rgba(0,0,0,0.11)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.07)]">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="block h-[14px] w-[110px] rounded-[6px] bg-[#BFBFC0]" />
                  </div>
                  <span className="flex-shrink-0 text-[10px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-[#EFEFF1] text-gray-600">#2 Waiting</span>
                </div>

                {/* Obfuscated card 2 */}
                <div className="flex items-center gap-[10px] p-[10px_14px] rounded-[16px] border border-[rgba(0,0,0,0.11)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.07)]">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="block h-[14px] w-[90px] rounded-[6px] bg-[#BFBFC0]" />
                  </div>
                  <span className="flex-shrink-0 text-[10px] font-semibold px-[8px] py-[3px] rounded-[5px] bg-[#EFEFF1] text-gray-600">#3 Waiting</span>
                </div>

              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[12px]">
                Privacy Mode on: your card shows your name; other participants appear anonymous
              </p>
            </div>

            <Tip>When you toggle a feature off, it is immediately hidden for all participants currently in the queue. Toggling it back on restores it instantly.</Tip>
          </section>

          {/* Meeting Links */}
          <section id="event-links" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.meetingLinksTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.meetingLinksP1" components={{ strong: <strong /> }} />
            </p>
            <Step n={1}>
              <Trans i18nKey="docs.meetingLinksStep1" components={{ strong: <strong /> }} />
            </Step>
            <Step n={2}>
              <Trans i18nKey="docs.meetingLinksStep2" components={{ strong: <strong /> }} />
            </Step>
            <Tip>
              <Trans i18nKey="docs.meetingLinksTip" components={{ code: <Code /> }} />
            </Tip>

            {/* Illustration — Event link pill with edit icons */}
            <div className="my-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex items-center gap-[8px]">
                <span className="inline-flex items-center gap-[5px] h-[36px] px-[14px] bg-[#1D1D1E] text-white rounded-[32px] font-body text-[12px] font-medium">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6" /><path d="M10 14L21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  </svg>
                  Join Zoom
                </span>
                <span className="text-[#818185]" title="Rename label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </span>
                <span className="text-[#818185]" title="Edit URL">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </span>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">
                Admin view: pencil renames the pill label, link icon edits the URL
              </p>
            </div>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.meetingLinksRenamingTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.meetingLinksRenamingP1')}
            </p>
            <Step n={1}>
              <Trans i18nKey="docs.meetingLinksRenamingStep1" />
            </Step>
            <Step n={2}>
              <Trans i18nKey="docs.meetingLinksRenamingStep2" components={{ strong: <strong /> }} />
            </Step>
            <Step n={3}>
              {t('docs.meetingLinksRenamingStep3')}
            </Step>

            {/* Illustration — Renaming the label inline */}
            <div className="my-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex items-center gap-[6px]">
                <span className="inline-flex items-center gap-[5px] h-[36px] px-[14px] bg-[#1D1D1E] text-white rounded-[32px] font-body text-[12px] font-medium">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6" /><path d="M10 14L21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  </svg>
                  Join Zoom
                </span>
                <div className="h-[36px] w-[140px] px-[12px] flex items-center bg-white border border-gray-700 rounded-[18px]">
                  <span className="font-body text-[13px] text-base-black">Join Zoom</span>
                </div>
                <span className="h-[36px] px-[12px] rounded-[18px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium flex items-center">Save</span>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">
                Inline label editing: type a new name and click Save
              </p>
            </div>

            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.meetingLinksEditUrlP1" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              {t('docs.meetingLinksP2')}
            </p>
          </section>

          {/* Password Protection */}
          <section id="password-protection" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.passwordProtectionTitle')}<ProBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.passwordProtectionP1')}
            </p>
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.settingPasswordTitle')}
            </h3>
            <Step n={1}>
              <Trans i18nKey="docs.settingPasswordStep1" components={{ strong: <strong /> }} />
            </Step>
            <Step n={2}>
              {t('docs.settingPasswordStep2')}
            </Step>
            <Tip>
              {t('docs.settingPasswordTip')}
            </Tip>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.joiningPasswordTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.joiningPasswordP1')}
            </p>

            {/* Illustration — Password prompt */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[360px] flex flex-col gap-[8px]">
                <div className="flex gap-[8px]">
                  <div className="flex-1 h-[44px] px-[16px] flex items-center bg-white border border-gray-700 rounded-[24px]">
                    <span className="font-body text-[16px] text-base-black">Jamie</span>
                  </div>
                  <span className="flex-shrink-0 h-[44px] px-[20px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[14px] font-medium flex items-center">Join</span>
                </div>
                <div className="h-[44px] px-[16px] flex items-center bg-white border border-gray-700 rounded-[24px]">
                  <span className="font-mono text-[16px] text-[#BFBFC0] tracking-[8px]">••••••</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.passwordPromptCaption')}</p>
            </div>
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.changingPasswordTitle')}
            </h3>
            <Step n={1}>
              {t('docs.changingPasswordStep1')}
            </Step>
            <Step n={2}>
              <Trans i18nKey="docs.changingPasswordStep2" components={{ strong: <strong /> }} />
            </Step>
            <Note>
              {t('docs.changingPasswordNote')}
            </Note>
          </section>

          {/* Invite Tracker */}
          <section id="invite-tracker" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.inviteTrackerTitle')}<ProBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.inviteTrackerP1')}
            </p>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.sendingQueueInvitesTitle')}
            </h3>
            <Step n={1}><Trans i18nKey="docs.sendingQueueInvitesStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.sendingQueueInvitesStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.sendingQueueInvitesStep3" components={{ strong: <strong /> }} /></Step>

            {/* Illustration — Invite modal email input with pills */}
            <div className="mb-[16px] mt-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px]">
                {/* Mock modal header */}
                <div className="flex items-center justify-between mb-[12px]">
                  <span className="font-display text-[15px] font-bold text-base-black">Invite Members</span>
                  <span className="w-[20px] h-[20px] rounded-full bg-[#EFEFF1] flex items-center justify-center text-[#818185]">
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                  </span>
                </div>
                {/* Mock divider */}
                <div className="flex items-center gap-[10px] mb-[10px]">
                  <div className="flex-1 h-px bg-[#E5E5ED]" />
                  <span className="font-body text-[11px] text-[#818185]">Or send invite</span>
                  <div className="flex-1 h-px bg-[#E5E5ED]" />
                </div>
                {/* Mock input */}
                <div className="h-[36px] rounded-[20px] border border-[#BCEBFB] bg-white px-[12px] flex items-center shadow-[0_0_0_3px_rgba(93,225,255,0.15)] mb-[8px]">
                  <span className="font-body text-[12px] text-[#BFBFC0]">Enter email address...</span>
                </div>
                {/* Hint text */}
                <p className="font-body text-[10px] text-[#818185] ml-[4px] mb-[8px]">Add multiple emails before sending. Press Enter or comma to add each one.</p>
                {/* Mock pills */}
                <div className="flex flex-wrap gap-[5px] mb-[10px]">
                  {['alex@example.com', 'taylor@example.com', 'jamie@example.com'].map((email) => (
                    <span key={email} className="flex items-center gap-[3px] bg-[#F5F5F6] border border-[#E5E5ED] rounded-[16px] pl-[8px] pr-[5px] py-[2px] font-body text-[11px] text-base-black">
                      {email}
                      <span className="w-[12px] h-[12px] rounded-full flex items-center justify-center text-[#818185]">
                        <svg width="6" height="6" viewBox="0 0 6 6" fill="none"><path d="M1 1l4 4M5 1L1 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
                      </span>
                    </span>
                  ))}
                </div>
                {/* Mock send button */}
                <div className="flex gap-[6px]">
                  <div className="flex-1 h-[32px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium flex items-center justify-center">Send (3)</div>
                  <div className="flex-1 h-[32px] rounded-[32px] bg-[#EFEFF1] border border-[#E5E5ED] font-body text-[12px] font-medium text-base-black flex items-center justify-center">Cancel</div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.inviteModalCaption')}</p>
            </div>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.inviteTrackerPillTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.inviteTrackerPillP1')}
            </p>

            {/* Illustration — Invite tracker pill + open popover */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[360px]">
                {/* Header action row mock */}
                <div className="flex items-center gap-[8px] mb-[10px]">
                  {/* Tracker pill — mixed state */}
                  <div className="h-[36px] px-[14px] rounded-[32px] bg-[#F5F5F6] border border-[#E5E5ED] font-body text-[13px] font-medium text-base-black flex items-center gap-[6px]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#818185]">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span>2/3 joined</span>
                  </div>
                  {/* Invite Members pill */}
                  <div className="h-[36px] px-[14px] rounded-[32px] border border-[#E5E5ED] bg-[#F5F5F6] font-body text-[13px] font-medium text-[#818185] flex items-center gap-[6px]">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>
                    Invite Members
                  </div>
                </div>

                {/* Popover panel */}
                <div className="rounded-[16px] bg-white border border-[#E5E5ED] shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-[14px]">
                  <p className="font-body text-[11px] font-semibold text-[#818185] uppercase tracking-wider mb-[10px]">
                    Invites · 2/3 joined
                  </p>
                  <div className="flex flex-col gap-[4px]">
                    {/* Joined row 1 */}
                    <div className="flex items-center gap-[10px] px-[8px] py-[6px] rounded-[8px]">
                      <span className="w-[7px] h-[7px] rounded-full bg-[#0ea5c9] flex-shrink-0" />
                      <span className="flex-1 font-mono text-[12px] text-base-black font-medium truncate">alex@example.com</span>
                      <span className="flex-shrink-0 font-body text-[11px] font-semibold text-[#0ea5c9]">Joined</span>
                    </div>
                    {/* Joined row 2 */}
                    <div className="flex items-center gap-[10px] px-[8px] py-[6px] rounded-[8px]">
                      <span className="w-[7px] h-[7px] rounded-full bg-[#0ea5c9] flex-shrink-0" />
                      <span className="flex-1 font-mono text-[12px] text-base-black font-medium truncate">taylor@example.com</span>
                      <span className="flex-shrink-0 font-body text-[11px] font-semibold text-[#0ea5c9]">Joined</span>
                    </div>
                    {/* Pending row */}
                    <div className="flex items-center gap-[10px] px-[8px] py-[6px] rounded-[8px]">
                      <span className="w-[7px] h-[7px] rounded-full bg-[#BFBFC0] flex-shrink-0" />
                      <span className="flex-1 font-mono text-[12px] text-[#818185] truncate">jamie@example.com</span>
                      <span className="flex-shrink-0 font-body text-[11px] font-semibold text-[#BFBFC0]">Pending</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.inviteTrackerCaption')}</p>
            </div>

            <Note>
              {t('docs.inviteTrackerNote')}
            </Note>
          </section>

          {/* QR Code Join */}
          <section id="qr-join" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.qrJoinTitle')}<ProBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.qrJoinP1')}
            </p>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.qrJoinHowTitle')}
            </h3>
            <Step n={1}><Trans i18nKey="docs.qrJoinStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.qrJoinStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.qrJoinStep3" components={{ strong: <strong /> }} /></Step>

            {/* Illustration — Modal QR tab + Fullscreen presenter */}
            <div className="mb-[24px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col tablet:flex-row gap-[14px] items-stretch">

                {/* Modal with QR tab */}
                <div className="flex-1 bg-white rounded-[12px] border border-[rgba(0,0,0,0.08)] shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-[14px]">
                  <div className="flex items-center justify-between mb-[10px]">
                    <span className="font-display text-[13px] font-bold text-base-black">Invite Members</span>
                    <div className="w-[18px] h-[18px] rounded-full bg-[#F5F5F6] flex items-center justify-center text-[#818185]">
                      <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                    </div>
                  </div>
                  <div className="flex gap-[4px] mb-[12px]">
                    <div className="flex-1 h-[26px] rounded-[32px] bg-[#F5F5F6] border border-[#E5E5ED] flex items-center justify-center font-body text-[10px] text-[#818185]">Email Invite</div>
                    <div className="flex-1 h-[26px] rounded-[32px] bg-[#1D1D1E] flex items-center justify-center font-body text-[10px] text-white">QR Code</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="p-[8px] rounded-[8px] border border-[rgba(0,0,0,0.08)] mb-[7px]" style={{ backgroundColor: '#FFFFFF' }}>
                      <svg width="72" height="72" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" style={{ fill: '#1D1D1E' }}>
                        <rect x="0" y="0" width="7" height="7"/>
                        <rect x="1" y="1" width="5" height="5" fill="white"/>
                        <rect x="2" y="2" width="3" height="3"/>
                        <rect x="14" y="0" width="7" height="7"/>
                        <rect x="15" y="1" width="5" height="5" fill="white"/>
                        <rect x="16" y="2" width="3" height="3"/>
                        <rect x="0" y="14" width="7" height="7"/>
                        <rect x="1" y="15" width="5" height="5" fill="white"/>
                        <rect x="2" y="16" width="3" height="3"/>
                        <rect x="8" y="6" width="1" height="1"/><rect x="10" y="6" width="1" height="1"/><rect x="12" y="6" width="1" height="1"/>
                        <rect x="6" y="8" width="1" height="1"/><rect x="6" y="10" width="1" height="1"/><rect x="6" y="12" width="1" height="1"/>
                        <rect x="8" y="0" width="1" height="1"/><rect x="10" y="0" width="1" height="1"/><rect x="12" y="0" width="1" height="1"/>
                        <rect x="9" y="1" width="2" height="1"/><rect x="8" y="2" width="1" height="1"/><rect x="11" y="2" width="2" height="1"/>
                        <rect x="9" y="3" width="1" height="1"/><rect x="8" y="4" width="2" height="1"/><rect x="12" y="4" width="1" height="1"/>
                        <rect x="8" y="5" width="1" height="1"/><rect x="11" y="5" width="1" height="1"/>
                        <rect x="14" y="8" width="1" height="1"/><rect x="16" y="8" width="2" height="1"/><rect x="20" y="8" width="1" height="1"/>
                        <rect x="15" y="9" width="1" height="1"/><rect x="18" y="9" width="1" height="1"/>
                        <rect x="14" y="10" width="2" height="1"/><rect x="17" y="10" width="2" height="1"/>
                        <rect x="16" y="11" width="1" height="1"/><rect x="19" y="11" width="2" height="1"/>
                        <rect x="14" y="12" width="1" height="1"/><rect x="17" y="12" width="1" height="1"/><rect x="20" y="12" width="1" height="1"/>
                        <rect x="8" y="14" width="2" height="1"/><rect x="11" y="14" width="1" height="1"/><rect x="13" y="14" width="1" height="1"/>
                        <rect x="9" y="15" width="1" height="1"/><rect x="12" y="15" width="2" height="1"/>
                        <rect x="8" y="16" width="1" height="1"/><rect x="10" y="16" width="3" height="1"/><rect x="14" y="16" width="1" height="1"/>
                        <rect x="9" y="17" width="1" height="1"/><rect x="12" y="17" width="1" height="1"/>
                        <rect x="8" y="18" width="3" height="1"/><rect x="13" y="18" width="2" height="1"/>
                        <rect x="9" y="19" width="2" height="1"/><rect x="12" y="19" width="1" height="1"/>
                        <rect x="8" y="20" width="1" height="1"/><rect x="11" y="20" width="3" height="1"/>
                        <rect x="7" y="8" width="1" height="1"/><rect x="8" y="8" width="1" height="1"/><rect x="10" y="8" width="2" height="1"/><rect x="13" y="8" width="1" height="1"/>
                        <rect x="9" y="9" width="1" height="1"/><rect x="12" y="9" width="1" height="1"/>
                        <rect x="7" y="10" width="2" height="1"/><rect x="11" y="10" width="1" height="1"/><rect x="13" y="10" width="1" height="1"/>
                        <rect x="8" y="11" width="1" height="1"/><rect x="10" y="11" width="3" height="1"/>
                        <rect x="7" y="12" width="1" height="1"/><rect x="9" y="12" width="2" height="1"/><rect x="12" y="12" width="2" height="1"/>
                        <rect x="0" y="8" width="1" height="1"/><rect x="2" y="8" width="2" height="1"/><rect x="5" y="8" width="1" height="1"/>
                        <rect x="1" y="9" width="1" height="1"/><rect x="4" y="9" width="1" height="1"/>
                        <rect x="0" y="10" width="2" height="1"/><rect x="3" y="10" width="1" height="1"/><rect x="5" y="10" width="1" height="1"/>
                        <rect x="1" y="11" width="2" height="1"/><rect x="4" y="11" width="2" height="1"/>
                        <rect x="0" y="12" width="1" height="1"/><rect x="3" y="12" width="1" height="1"/><rect x="5" y="12" width="1" height="1"/>
                      </svg>
                    </div>
                    <p className="font-mono text-[9px] text-[#818185] mb-[9px] text-center">standby.app/review/6f218712</p>
                    <div className="flex gap-[4px] w-full">
                      <div className="flex-1 h-[28px] rounded-[32px] bg-[#F5F5F6] border border-[#E5E5ED] flex items-center justify-center gap-[3px] font-body text-[10px] font-medium text-base-black">
                        <svg width="9" height="9" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        Download
                      </div>
                      <div className="flex-1 h-[28px] rounded-[32px] bg-[#1D1D1E] flex items-center justify-center gap-[3px] font-body text-[10px] font-medium text-white">
                        <svg width="9" height="9" viewBox="0 0 16 16" fill="none"><rect x="1" y="2" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M8 12v2M5 14h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        Present
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fullscreen presenter */}
                <div className="flex-1 bg-[#111113] rounded-[12px] p-[14px] flex flex-col items-center justify-center relative">
                  <div className="absolute top-[8px] right-[8px] w-[24px] h-[24px] rounded-full bg-white/10 flex items-center justify-center">
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="white" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  </div>
                  <div className="p-[10px] rounded-[10px] mb-[10px]" style={{ backgroundColor: '#FFFFFF' }}>
                    <svg width="90" height="90" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" style={{ fill: '#1D1D1E' }}>
                      <rect x="0" y="0" width="7" height="7"/>
                      <rect x="1" y="1" width="5" height="5" fill="white"/>
                      <rect x="2" y="2" width="3" height="3"/>
                      <rect x="14" y="0" width="7" height="7"/>
                      <rect x="15" y="1" width="5" height="5" fill="white"/>
                      <rect x="16" y="2" width="3" height="3"/>
                      <rect x="0" y="14" width="7" height="7"/>
                      <rect x="1" y="15" width="5" height="5" fill="white"/>
                      <rect x="2" y="16" width="3" height="3"/>
                      <rect x="8" y="6" width="1" height="1"/><rect x="10" y="6" width="1" height="1"/><rect x="12" y="6" width="1" height="1"/>
                      <rect x="6" y="8" width="1" height="1"/><rect x="6" y="10" width="1" height="1"/><rect x="6" y="12" width="1" height="1"/>
                      <rect x="8" y="0" width="1" height="1"/><rect x="10" y="0" width="1" height="1"/><rect x="12" y="0" width="1" height="1"/>
                      <rect x="9" y="1" width="2" height="1"/><rect x="8" y="2" width="1" height="1"/><rect x="11" y="2" width="2" height="1"/>
                      <rect x="9" y="3" width="1" height="1"/><rect x="8" y="4" width="2" height="1"/><rect x="12" y="4" width="1" height="1"/>
                      <rect x="8" y="5" width="1" height="1"/><rect x="11" y="5" width="1" height="1"/>
                      <rect x="14" y="8" width="1" height="1"/><rect x="16" y="8" width="2" height="1"/><rect x="20" y="8" width="1" height="1"/>
                      <rect x="15" y="9" width="1" height="1"/><rect x="18" y="9" width="1" height="1"/>
                      <rect x="14" y="10" width="2" height="1"/><rect x="17" y="10" width="2" height="1"/>
                      <rect x="16" y="11" width="1" height="1"/><rect x="19" y="11" width="2" height="1"/>
                      <rect x="14" y="12" width="1" height="1"/><rect x="17" y="12" width="1" height="1"/><rect x="20" y="12" width="1" height="1"/>
                      <rect x="8" y="14" width="2" height="1"/><rect x="11" y="14" width="1" height="1"/><rect x="13" y="14" width="1" height="1"/>
                      <rect x="9" y="15" width="1" height="1"/><rect x="12" y="15" width="2" height="1"/>
                      <rect x="8" y="16" width="1" height="1"/><rect x="10" y="16" width="3" height="1"/><rect x="14" y="16" width="1" height="1"/>
                      <rect x="9" y="17" width="1" height="1"/><rect x="12" y="17" width="1" height="1"/>
                      <rect x="8" y="18" width="3" height="1"/><rect x="13" y="18" width="2" height="1"/>
                      <rect x="9" y="19" width="2" height="1"/><rect x="12" y="19" width="1" height="1"/>
                      <rect x="8" y="20" width="1" height="1"/><rect x="11" y="20" width="3" height="1"/>
                      <rect x="7" y="8" width="1" height="1"/><rect x="8" y="8" width="1" height="1"/><rect x="10" y="8" width="2" height="1"/><rect x="13" y="8" width="1" height="1"/>
                      <rect x="9" y="9" width="1" height="1"/><rect x="12" y="9" width="1" height="1"/>
                      <rect x="7" y="10" width="2" height="1"/><rect x="11" y="10" width="1" height="1"/><rect x="13" y="10" width="1" height="1"/>
                      <rect x="8" y="11" width="1" height="1"/><rect x="10" y="11" width="3" height="1"/>
                      <rect x="7" y="12" width="1" height="1"/><rect x="9" y="12" width="2" height="1"/><rect x="12" y="12" width="2" height="1"/>
                      <rect x="0" y="8" width="1" height="1"/><rect x="2" y="8" width="2" height="1"/><rect x="5" y="8" width="1" height="1"/>
                      <rect x="1" y="9" width="1" height="1"/><rect x="4" y="9" width="1" height="1"/>
                      <rect x="0" y="10" width="2" height="1"/><rect x="3" y="10" width="1" height="1"/><rect x="5" y="10" width="1" height="1"/>
                      <rect x="1" y="11" width="2" height="1"/><rect x="4" y="11" width="2" height="1"/>
                      <rect x="0" y="12" width="1" height="1"/><rect x="3" y="12" width="1" height="1"/><rect x="5" y="12" width="1" height="1"/>
                    </svg>
                  </div>
                  <p className="font-mono text-[9px] text-white/40 mb-[5px] text-center">standby.app/review/6f218712</p>
                  <p className="font-body text-[9px] text-white/25">{t('inviteModal.pressEscToClose')}</p>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.qrJoinCaption')}</p>
            </div>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.qrJoinPresenterTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.qrJoinPresenterP1')}
            </p>

            <Tip>
              {t('docs.qrJoinTip')}
            </Tip>

            <Note>
              {t('docs.qrJoinNote')}
            </Note>
          </section>

          {/* Joining a Queue */}
          <section id="joining-queue" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.joiningReviewH2')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.joiningReviewP1')}
            </p>
            <Step n={1}>{t('docs.joiningReviewStep1')}</Step>
            <Step n={2}><Trans i18nKey="docs.joiningReviewStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}>
              {t('docs.joiningReviewStep3')}
            </Step>
            {/* Illustration — Joining: name entry + status banner */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[360px] flex flex-col gap-[12px]">
                <div className="flex gap-[8px]">
                  <div className="flex-1 h-[44px] px-[16px] flex items-center bg-white border border-[#BFBFC0] rounded-[24px] shadow-[0_0_0_4px_rgba(29,29,30,0.12)]">
                    <span className="font-body text-[16px] text-base-black">Jamie</span>
                    <span className="w-[1.5px] h-[16px] bg-base-black ml-[1px] inline-block" />
                  </div>
                  <span className="flex-shrink-0 h-[44px] px-[20px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[14px] font-medium flex items-center">Join</span>
                </div>
                <div className="flex items-center gap-[8px]">
                  <div className="flex-1 h-[1px] bg-[rgba(0,0,0,0.06)]" />
                  <span className="font-body text-[10px] text-[#BFBFC0]">after joining</span>
                  <div className="flex-1 h-[1px] bg-[rgba(0,0,0,0.06)]" />
                </div>
                <div className="p-[16px] rounded-[16px] bg-yellow-400">
                  <p className="font-body text-[14px] text-center font-semibold text-yellow-800">On Standby: #2 in queue</p>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.joiningReviewCaption')}</p>
            </div>
            <Tip>
              {t('docs.joiningReviewTip')}
            </Tip>
            <Note>
              {t('docs.joiningReviewNote')}
            </Note>

            {/* Unique Names */}
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mt-[20px] mb-[8px] font-semibold">
              {t('docs.uniqueNamesTitle')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.uniqueNamesP1')}
            </p>
            {/* Illustration — Name taken modal */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[340px] mx-auto">
                <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_12px_40px_rgba(0,0,0,0.1)] p-[20px] flex flex-col gap-[12px]">
                  <div>
                    <p className="font-display text-[16px] font-bold text-base-black mb-[2px]">Name Already Taken</p>
                    <p className="font-body text-[12px] text-gray-900 leading-[1.4]">"Jamie" is already used by someone in this queue.</p>
                  </div>
                  <p className="font-body text-[10px] text-[#818185] font-medium">Suggested names:</p>
                  <div className="flex flex-col gap-[6px]">
                    <div className="h-[36px] rounded-[10px] border border-[#0CB3D8] bg-[#F8FCFF] font-body text-[12px] font-medium text-base-black flex items-center px-[12px]">Jamie 2</div>
                    <div className="h-[36px] rounded-[10px] border border-[rgba(0,0,0,0.11)] bg-[#F5F5F6] font-body text-[12px] font-medium text-base-black flex items-center px-[12px]">Jamie 3</div>
                    <div className="h-[36px] rounded-[10px] border border-[rgba(0,0,0,0.11)] bg-[#F5F5F6] font-body text-[12px] font-medium text-base-black flex items-center px-[12px]">Jamie 4</div>
                  </div>
                  <div className="border-t border-[#E5E5ED] pt-[10px]">
                    <p className="font-body text-[10px] text-[#818185] font-medium mb-[6px]">Or enter your own:</p>
                    <div className="flex gap-[6px] items-center">
                      <div className="flex-1 h-[34px] px-[12px] flex items-center bg-white border border-[#BFBFC0] rounded-[20px]">
                        <span className="font-body text-[12px] text-[#BFBFC0]">Enter a different name</span>
                      </div>
                      <span className="flex-shrink-0 h-[34px] px-[12px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[11px] font-medium flex items-center">Join</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.uniqueNamesCaption')}</p>
            </div>

            {/* Renaming Yourself */}
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mt-[20px] mb-[8px] font-semibold">
              {t('docs.renameTitle')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.renameP1" components={{ strong: <strong /> }} />
            </p>
            <Step n={1}><Trans i18nKey="docs.renameStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.renameStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.renameStep3" components={{ strong: <strong /> }} /></Step>
            {/* Illustration — Your participant card with pencil icon */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[360px] flex flex-col gap-[8px]">
                {/* Card in view mode with pencil */}
                <div className="flex items-center gap-[10px] p-[12px_14px] rounded-[16px] border border-[#BFBFC0] bg-[#F5F5F6] shadow-[0_2px_10px_rgba(42,60,68,0.08)]">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">JA</span>
                  <span className="font-body text-[15px] font-semibold text-[#111827]">Jamie</span>
                  <span className="flex-shrink-0 p-[2px] rounded-[4px] text-[#818185] bg-[#E5E5ED]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                  </span>
                  <span className="text-[9px] font-medium text-[#4A4A4C] bg-[#EFEFF1] px-[5px] py-[1px] rounded-[3px]">You</span>
                  <span className="ml-auto font-mono text-[12px] text-gray-700 flex-shrink-0">#2</span>
                  <span className="text-[10px] font-semibold px-[6px] py-[2px] rounded-[4px] min-w-[55px] text-center bg-yellow-400 text-yellow-700 flex-shrink-0">On Standby</span>
                </div>
                {/* Arrow indicating edit */}
                <div className="flex items-center gap-[8px] ml-[54px]">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#818185" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>
                  <span className="font-body text-[10px] text-[#818185]">click pencil to edit</span>
                </div>
                {/* Card in edit mode */}
                <div className="flex items-center gap-[10px] p-[12px_14px] rounded-[16px] border border-[#BFBFC0] bg-[#F5F5F6] shadow-[0_2px_10px_rgba(42,60,68,0.08)]">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">JA</span>
                  <div className="flex-1 h-[30px] px-[8px] flex items-center bg-white border border-[#0CB3D8] rounded-[8px] shadow-[0_0_0_3px_rgba(93,225,255,0.20)]">
                    <span className="font-body text-[15px] font-semibold text-[#111827]">Jamie A</span>
                    <span className="w-[1.5px] h-[14px] bg-[#0CB3D8] ml-[1px] inline-block" />
                  </div>
                  <span className="ml-auto font-mono text-[12px] text-gray-700 flex-shrink-0">#2</span>
                  <span className="text-[10px] font-semibold px-[6px] py-[2px] rounded-[4px] min-w-[55px] text-center bg-yellow-400 text-yellow-700 flex-shrink-0">On Standby</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.renameCaption')}</p>
            </div>
            <Note>
              {t('docs.renameNote')}
            </Note>
          </section>

          {/* Wait Time Estimates */}
          <section id="wait-estimates" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              Wait Time Estimates<EnterpriseBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              When enabled, participants see a live countdown timer showing their estimated wait time. The estimate is calculated from the average time previous participants spent in the active (in-meeting) state.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Estimates appear after at least <strong>2 participants</strong> have completed the active-to-done cycle, giving the system enough data for a meaningful average.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              The countdown updates in real-time. When a new participant completes their turn, all waiting participants' estimates recalculate automatically via WebSocket.
            </p>

            {/* Illustration — Participant card with wait estimate */}
            <div className="p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[360px] flex flex-col gap-[6px]">
                <div className="flex items-center gap-[8px] p-[10px_12px] rounded-[16px] border border-[rgba(0,0,0,0.09)] bg-white">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">AC</span>
                  <span className="flex-1 font-body text-[15px] font-semibold text-[#111827] truncate">Alex Chen</span>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="font-mono text-[14px] font-medium text-gray-700">#2</span>
                    <span className="font-mono text-[10px] text-[#818185]">~3:24</span>
                  </div>
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center bg-yellow-400 text-yellow-700 flex-shrink-0">On Standby</span>
                </div>
                <div className="flex items-center gap-[8px] p-[10px_12px] rounded-[16px] border border-[rgba(0,0,0,0.09)] bg-white">
                  <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold flex-shrink-0">JL</span>
                  <span className="flex-1 font-body text-[15px] font-semibold text-[#111827] truncate">Jordan Lee</span>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="font-mono text-[14px] font-medium text-gray-700">#3</span>
                    <span className="font-mono text-[10px] text-[#818185]">~6:48</span>
                  </div>
                  <span className="text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center bg-gray-300 text-gray-600 flex-shrink-0">Waiting</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">Participant cards showing live wait countdown timers (~3:24 and ~6:48) based on the average meeting duration.</p>
            </div>

            <Note>Wait estimates are based on the average time participants spend in the active state. Short or unusually long meetings will shift the average; estimates become more accurate as more participants complete their turns.</Note>
          </section>

          {/* Leaving Queues & Teams */}
          <section id="leaving" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.leavingTitle')}
            </h2>

            <h3 className="font-body text-[16px] font-semibold text-base-black mb-[8px]">{t('docs.leavingQueueSubtitle')}</h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.leavingQueueP1')}
            </p>
            <Step n={1}>{t('docs.leavingQueueStep1')}</Step>
            <Step n={2}>{t('docs.leavingQueueStep2')}</Step>

            {/* Leave Queue illustration */}
            <div className="mb-[20px] p-[16px] rounded-[12px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col gap-[10px] max-w-[340px] mx-auto">
                {/* Leave Queue pill */}
                <div className="flex justify-center">
                  <span className="inline-flex items-center gap-[6px] h-[32px] px-[14px] rounded-[32px] bg-[#EFEFF1] border border-[#E5E5ED] font-body text-[12px] font-medium text-[#818185]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {t('review.leaveQueue')}
                  </span>
                </div>
                {/* Arrow */}
                <div className="flex justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BFBFC0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>
                </div>
                {/* Confirmation modal mini */}
                <div className="p-[16px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.11)] shadow-[0_2px_8px_rgba(0,0,0,0.08)] text-center">
                  <p className="font-display text-[14px] font-bold text-base-black mb-[4px]">{t('review.leaveQueue')}</p>
                  <p className="font-body text-[11px] text-[#818185] mb-[10px]">{t('docs.leavingConfirmDesc')}</p>
                  <div className="flex gap-[6px] justify-center">
                    <span className="inline-flex items-center h-[28px] px-[12px] rounded-[32px] bg-[#D8210C] text-white text-[11px] font-medium">{t('review.confirmLeave')}</span>
                    <span className="inline-flex items-center h-[28px] px-[12px] rounded-[32px] bg-[#EFEFF1] border border-[#E5E5ED] text-[#4A4A4C] text-[11px] font-medium">{t('common.cancel')}</span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.leavingQueueCaption')}</p>
            </div>

            <Note>{t('docs.leavingQueueNote')}</Note>

            <h3 className="font-body text-[16px] font-semibold text-base-black mb-[8px] mt-[24px]">{t('docs.leavingTeamSubtitle')}</h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.leavingTeamP1')}
            </p>
            <Step n={1}>{t('docs.leavingTeamStep1')}</Step>
            <Step n={2}>{t('docs.leavingTeamStep2')}</Step>

            {/* Leave Team illustration */}
            <div className="mb-[20px] p-[16px] rounded-[12px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex items-center gap-[8px] flex-wrap max-w-[400px]">
                <span className="inline-flex items-center gap-[6px] h-[36px] px-[14px] rounded-[32px] bg-[#EFEFF1] border border-[#E5E5ED] font-body text-[13px] font-medium text-[#818185]">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  {t('teams.leaveTeam')}
                </span>
                <span className="font-body text-[12px] text-[#BFBFC0]">&larr;</span>
                <span className="font-body text-[12px] text-[#818185] italic">{t('docs.leavingTeamCaption')}</span>
              </div>
            </div>

            <Note>{t('docs.leavingTeamNote')}</Note>

            <h3 className="font-body text-[16px] font-semibold text-base-black mb-[8px] mt-[24px]">{t('docs.endQueueSubtitle')}</h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.endQueueP1')}
            </p>

            {/* End Queue illustration */}
            <div className="mb-[20px] p-[16px] rounded-[12px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col gap-[10px] max-w-[340px] mx-auto">
                <div className="flex justify-center">
                  <span className="inline-flex items-center h-[36px] px-[14px] rounded-[32px] border border-[#D8210C] bg-white font-body text-[13px] font-medium text-[#D8210C]">
                    {t('review.endReview')}
                  </span>
                </div>
                <div className="flex justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BFBFC0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>
                </div>
                <div className="p-[16px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.11)] shadow-[0_2px_8px_rgba(0,0,0,0.08)] text-center">
                  <p className="font-display text-[14px] font-bold text-base-black mb-[4px]">{t('review.endReview')}</p>
                  <p className="font-body text-[11px] text-[#818185] mb-[10px]">{t('docs.endQueueConfirmDesc')}</p>
                  <div className="flex gap-[6px] justify-center">
                    <span className="inline-flex items-center h-[28px] px-[12px] rounded-[32px] bg-[#D8210C] text-white text-[11px] font-medium">{t('review.endReviewConfirm')}</span>
                    <span className="inline-flex items-center h-[28px] px-[12px] rounded-[32px] bg-[#EFEFF1] border border-[#E5E5ED] text-[#4A4A4C] text-[11px] font-medium">{t('common.cancel')}</span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.endQueueCaption')}</p>
            </div>

            <Tip>{t('docs.endQueueTip')}</Tip>
          </section>

          {/* Meeting Status */}
          <section id="event-status" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.meetingStatusTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.meetingStatusP1')}
            </p>

            <div className="mb-[16px] p-[16px] rounded-[12px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col gap-[8px]">
                <div className="flex items-center gap-[8px]">
                  <span className="inline-flex items-center gap-[6px] px-[12px] py-[5px] rounded-[32px] bg-green-500 text-green-900 text-[13px] font-semibold border border-green-900/30 animate-pulseBorder">
                    <span className="w-[8px] h-[8px] rounded-full bg-green-900 animate-pulseStrong" />
                    {t('docs.statusInProgress')}
                  </span>
                  <span className="font-body text-[13px] text-[#374151]">{t('docs.statusInProgressDesc')}</span>
                </div>
                <div className="flex items-center gap-[8px]">
                  <span className="inline-flex items-center gap-[6px] px-[12px] py-[5px] rounded-[32px] bg-[#DFDFE2] dark:bg-[#3A3A3D] text-[#58585C] dark:text-[#E0E0E3] text-[13px] font-semibold border border-[rgba(0,0,0,0.10)] dark:border-[rgba(255,255,255,0.1)]">
                    <span className="w-[8px] h-[8px] rounded-full bg-[#97979B] dark:bg-[#6B6B6F]" />
                    {t('docs.statusWaitingLabel')}
                  </span>
                  <span className="font-body text-[13px] text-[#374151]">{t('docs.statusWaitingDesc')}</span>
                </div>
              </div>
            </div>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.forAdminsTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.forAdminsP1')}
            </p>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.forParticipantsTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              {t('docs.forParticipantsP1')}
            </p>
          </section>

          {/* Teams */}
          <section id="teams" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.teamsTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.teamsP1" components={{ strong: <strong /> }} />
            </p>

            <div className="mb-[16px] p-[16px] rounded-[12px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col gap-[8px]">
                <div>
                  <p className="font-body text-[14px] font-semibold text-base-black">{t('docs.teamRoleAdmin')}</p>
                  <p className="font-body text-[13px] text-[#374151]">
                    {t('docs.teamRoleAdminDesc')}
                  </p>
                </div>
                <div>
                  <p className="font-body text-[14px] font-semibold text-base-black">{t('docs.teamRoleMember')}</p>
                  <p className="font-body text-[13px] text-[#374151]">
                    {t('docs.teamRoleMemberDesc')}
                  </p>
                </div>
              </div>
            </div>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.creatingTeamTitle')}
            </h3>
            <Step n={1}><Trans i18nKey="docs.creatingTeamStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.creatingTeamStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}>{t('docs.creatingTeamStep3')}</Step>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.invitingMembersTitle')}
            </h3>
            <Note>{t('docs.invitingMembersNote')}</Note>
            <Step n={1}><Trans i18nKey="docs.invitingMembersStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}>
              {t('docs.invitingMembersStep2')}
            </Step>
            <Step n={3}>
              {t('docs.invitingMembersStep3')}
            </Step>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.teamJoinPasswordTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.teamJoinPasswordP1')}
            </p>
            <Step n={1}><Trans i18nKey="docs.teamJoinPasswordStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.teamJoinPasswordStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.teamJoinPasswordStep3" components={{ strong: <strong /> }} /></Step>
            <Tip>
              {t('docs.teamJoinPasswordTip')}
            </Tip>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.managingMembersTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.managingMembersP1" components={{ strong: <strong /> }} />
            </p>
            <h4 className="font-display text-[16px] font-bold text-base-black mb-[8px]">
              {t('docs.changingRoleTitle')}
            </h4>
            <Step n={1}><Trans i18nKey="docs.changingRoleStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.changingRoleStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}>{t('docs.changingRoleStep3')}</Step>
            <Note>
              {t('docs.changingRoleNote')}
            </Note>

            <h4 className="font-display text-[16px] font-bold text-base-black mb-[8px] mt-[24px]">
              {t('docs.removingMemberTitle')}
            </h4>
            <Step n={1}><Trans i18nKey="docs.removingMemberStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.removingMemberStep2" components={{ strong: <strong /> }} /></Step>
            <Note>
              {t('docs.removingMemberNote')}
            </Note>
          </section>

          {/* Team Reviews */}
          <section id="team-queues" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.teamReviewsTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.teamReviewsP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              {t('docs.teamReviewsP2')}
            </p>
          </section>

          {/* Multiple Teams per Review */}
          <section id="multi-team" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.multiTeamTitle')}<ProBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.multiTeamP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.multiTeamP2')}
            </p>
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.multiTeamAdminTitle')}
            </h3>
            <Step n={1}>
              <Trans i18nKey="docs.multiTeamAdminStep1" components={{ strong: <strong /> }} />
            </Step>
            <Step n={2}>
              <Trans i18nKey="docs.multiTeamAdminStep2" components={{ strong: <strong /> }} />
            </Step>
            <Step n={3}>
              <Trans i18nKey="docs.multiTeamAdminStep3" components={{ strong: <strong /> }} />
            </Step>
            <Note>
              {t('docs.multiTeamAdminNote')}
            </Note>
          </section>

          {/* Chat */}
          <section id="chat" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.chatTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.chatP1')}
            </p>
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.mentionsTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.mentionsP1" components={{ code: <Code /> }} />
            </p>
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              {t('docs.unreadBadgeTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              {t('docs.unreadBadgeP1')}
            </p>
            {/* Illustration — Chat: bubbles with @mention + FAB with unread badge */}
            <div className="mt-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px] flex flex-col gap-[12px]">
                <div className="bg-white rounded-[12px] border border-[rgba(0,0,0,0.09)] p-[12px] flex flex-col gap-[10px]">
                  <div className="flex flex-col items-start gap-[2px]">
                    <span className="font-body text-[11px] text-gray-700 px-[4px]">Alex</span>
                    <div className="max-w-[210px] px-[12px] py-[8px] rounded-[14px] rounded-bl-[4px] bg-gray-400 text-base-black font-body text-[14px]">Starting in 5 minutes</div>
                  </div>
                  <div className="flex flex-col items-start gap-[2px]">
                    <span className="font-body text-[11px] text-gray-700 px-[4px]">Sam</span>
                    <div className="max-w-[210px] px-[12px] py-[8px] rounded-[14px] rounded-bl-[4px] bg-gray-400 text-base-black font-body text-[14px]">
                      <span className="font-semibold px-[4px] py-[1px] rounded-[4px] bg-[#0ea5c9] text-white">@Jamie</span>{' '}you're up next
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-[10px]">
                  <div className="relative flex-shrink-0">
                    <div className="w-[56px] h-[56px] rounded-full bg-[#1D1D1E] text-white shadow-[0_4px_20px_rgba(0,0,0,0.2)] flex items-center justify-center">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                    </div>
                    <span className="absolute -top-[4px] -right-[4px] w-[20px] h-[20px] rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                  </div>
                  <span className="font-body text-[12px] text-[#818185]">Chat button with unread count</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.chatMentionsCaption')}</p>
            </div>
          </section>

          {/* Notifications */}
          <section id="notifications" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.notificationsTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.notificationsP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              {t('docs.notificationsP2')}
            </p>
            <ul className="list-disc ml-[20px] mb-[16px] font-body text-[14px] text-[#374151] leading-relaxed flex flex-col gap-[4px]">
              <li><Trans i18nKey="docs.notifOnStandby" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.notifNextUp" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.notifYourTurn" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.notifMention" components={{ strong: <strong /> }} /></li>
            </ul>
            {/* Illustration — Browser notification toast */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[300px]">
                <div className="bg-white rounded-[12px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.10)] p-[12px_14px] flex items-start gap-[10px]">
                  <div className="w-[32px] h-[32px] rounded-[8px] bg-[#1D1D1E] flex items-center justify-center flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] text-[#818185]">Standby</p>
                    <p className="font-body text-[13px] font-semibold text-base-black">It's your turn!</p>
                    <p className="font-body text-[12px] text-[#818185]">Sprint Review: you're up next</p>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.notificationToastCaption')}</p>
            </div>
            <Note>
              {t('docs.notificationsNote')}
            </Note>
          </section>

          {/* Co-Admins */}
          <section id="co-admins" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.coAdminsTitle')}<ProBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.coAdminsP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.coAdminsCan" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.ownerCan" components={{ strong: <strong /> }} />
            </p>
            <Step n={1}><Trans i18nKey="docs.coAdminsStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.coAdminsStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.coAdminsStep3" components={{ strong: <strong /> }} /></Step>
            <Note>
              {t('docs.coAdminsNote')}
            </Note>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mt-[16px] mb-[8px] font-semibold">
              {t('docs.coAdminsPendingTitle')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              {t('docs.coAdminsPendingP1')}
            </p>
            <ul className="list-disc pl-[24px] space-y-[4px] font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <li><Trans i18nKey="docs.coAdminsPendingResend" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.coAdminsPendingRevoke" components={{ strong: <strong /> }} /></li>
            </ul>
          </section>

          {/* Queue Templates */}
          <section id="queue-templates" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              Queue Templates<EnterpriseBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Enterprise company admins can save a queue's configuration as a <strong>reusable template</strong>. This includes the queue title, meeting URL, participant limit, password, and all feature toggle settings.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              To save a template, open <strong>Queue Settings → Template</strong> tab, enter a name, and click <strong>Save Template</strong>. The template is saved to your company and can be used when creating new queues.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              When creating a new queue, company members can select a saved template from the dropdown to pre-fill all settings automatically.
            </p>

            {/* Illustration — Template save form */}
            <div className="p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px] flex flex-col gap-[10px]">
                <p className="font-body text-[11px] text-[#818185]">Save this queue's settings as a reusable template for your company.</p>
                <div className="flex items-center gap-[8px]">
                  <div className="flex-1 h-[36px] px-[14px] flex items-center bg-white border border-[#BFBFC0] rounded-[24px]">
                    <span className="font-body text-[13px] text-base-black">Weekly Standup</span>
                    <span className="w-[1.5px] h-[14px] bg-base-black ml-[1px] inline-block" />
                  </div>
                  <span className="h-[36px] px-[14px] bg-[#1D1D1E] text-white rounded-[32px] font-body text-[12px] font-medium flex items-center justify-center">Save Template</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">The Template tab in Queue Settings. Enter a name and save the current queue configuration as a reusable template.</p>
            </div>
          </section>

          {/* Company & Enterprise */}
          <section id="company" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              Company & Enterprise<EnterpriseBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              The Enterprise tier adds company-centric features for organizations. A company is a shared workspace with branded queue experiences, member management, and advanced queue features.
            </p>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              Company Dashboard
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Enterprise members can access their company dashboard from the <strong>Company</strong> link in the navigation menu. The dashboard includes tabs for:
            </p>
            <ul className="list-disc list-inside font-body text-[14px] text-[#374151] mb-[16px] space-y-[4px]">
              <li><strong>Members</strong>: view and manage company members and roles</li>
              <li><strong>Branding</strong>: customize your company logo, colors, and appearance</li>
              <li><strong>Templates</strong>: manage saved queue templates</li>
              <li><strong>Feature Toggles</strong>: set default feature toggles for new company queues</li>
              <li><strong>My QR</strong>: view and download your personal QR code for check-in</li>
            </ul>

            {/* Illustration — Company dashboard tabs */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[400px]">
                <div className="flex items-center gap-[8px] mb-[14px]">
                  <span className="w-[28px] h-[28px] rounded-[6px] bg-[#7C3AED] flex items-center justify-center text-white text-[12px] font-bold">A</span>
                  <span className="font-display text-[16px] font-bold text-base-black">Acme Corp</span>
                </div>
                <div className="flex gap-[4px] mb-[14px]">
                  <span className="h-[28px] px-[10px] rounded-[8px] bg-[#1D1D1E] text-white font-body text-[11px] font-medium flex items-center">Members</span>
                  <span className="h-[28px] px-[10px] rounded-[8px] bg-[#EFEFF1] text-[#818185] font-body text-[11px] font-medium flex items-center">Branding</span>
                  <span className="h-[28px] px-[10px] rounded-[8px] bg-[#EFEFF1] text-[#818185] font-body text-[11px] font-medium flex items-center">Templates</span>
                  <span className="h-[28px] px-[10px] rounded-[8px] bg-[#EFEFF1] text-[#818185] font-body text-[11px] font-medium flex items-center">My QR</span>
                </div>
                {/* Member rows */}
                <div className="flex flex-col gap-[4px]">
                  <div className="flex items-center gap-[10px] p-[8px_12px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.09)]">
                    <span className="w-[28px] h-[28px] rounded-full bg-[#7C3AED] text-white text-[10px] font-semibold flex items-center justify-center">JD</span>
                    <span className="flex-1 font-body text-[13px] font-medium text-base-black">Jane Doe</span>
                    <span className="text-[10px] font-semibold px-[6px] py-[2px] rounded-[4px] bg-[#F5F3FF] text-[#7C3AED]">Admin</span>
                  </div>
                  <div className="flex items-center gap-[10px] p-[8px_12px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.09)]">
                    <span className="w-[28px] h-[28px] rounded-full bg-[#1D1D1E] text-white text-[10px] font-semibold flex items-center justify-center">AS</span>
                    <span className="flex-1 font-body text-[13px] font-medium text-base-black">Alex Smith</span>
                    <span className="text-[10px] font-semibold px-[6px] py-[2px] rounded-[4px] bg-[#EFEFF1] text-[#818185]">Member</span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">The Company Dashboard showing the Members tab with role badges. Admins can manage members and configure company settings.</p>
            </div>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              Company Branding & Subsites
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Companies can customize their branding with a <strong>logo</strong>, <strong>primary color</strong>, <strong>secondary color</strong>, and <strong>accent color</strong>. These colors are applied to all queues created under the company.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              Company queues are accessible via branded subsites at <Code>/c/your-company-slug</Code>. The subsite shows the company logo in the header with custom colors throughout.
            </p>

            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[24px]">
              QR Check-in
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              When QR Check-in is enabled on a queue, each participant receives a unique QR code when they join. This QR code can be scanned by an admin to verify attendance at in-person events.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              Enterprise members also have a <strong>personal QR code</strong> on the My QR tab of the Company Dashboard. This permanent QR code is linked to their company membership and can be downloaded or presented fullscreen.
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              QR codes are generated and cleared in real-time via WebSocket. When an admin toggles QR Check-in on, all waiting participants immediately receive their codes without refreshing.
            </p>

            {/* Illustration — QR check-in card */}
            <div className="p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[280px]">
                <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-[16px] flex flex-col items-center gap-[10px]">
                  <span className="font-body text-[13px] font-semibold text-base-black">Your Check-in Code</span>
                  {/* QR code placeholder */}
                  <div className="w-[100px] h-[100px] rounded-[8px] bg-[#F5F5F6] border border-[#E5E5ED] flex items-center justify-center">
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <rect x="4" y="4" width="18" height="18" rx="2" fill="#1D1D1E"/><rect x="7" y="7" width="12" height="12" rx="1" fill="white"/><rect x="10" y="10" width="6" height="6" fill="#1D1D1E"/>
                      <rect x="38" y="4" width="18" height="18" rx="2" fill="#1D1D1E"/><rect x="41" y="7" width="12" height="12" rx="1" fill="white"/><rect x="44" y="10" width="6" height="6" fill="#1D1D1E"/>
                      <rect x="4" y="38" width="18" height="18" rx="2" fill="#1D1D1E"/><rect x="7" y="41" width="12" height="12" rx="1" fill="white"/><rect x="10" y="44" width="6" height="6" fill="#1D1D1E"/>
                      <rect x="26" y="26" width="8" height="8" fill="#1D1D1E"/><rect x="38" y="38" width="4" height="4" fill="#1D1D1E"/><rect x="46" y="38" width="4" height="4" fill="#1D1D1E"/><rect x="38" y="46" width="4" height="4" fill="#1D1D1E"/>
                    </svg>
                  </div>
                  <span className="font-mono text-[10px] text-[#818185]">Show this to the admin for check-in</span>
                  <span className="inline-flex items-center gap-[4px] h-[28px] px-[12px] rounded-[32px] border border-[#E5E5ED] bg-[#EFEFF1] font-body text-[11px] font-medium text-[#818185]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>
                    Present Fullscreen
                  </span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">A participant's QR check-in card. Tap "Present Fullscreen" to show the code for scanning. The code is unique to each participant and queue.</p>
            </div>
          </section>

          {/* Settings */}
          <section id="settings" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.settingsTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.settingsP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.settingsAccount" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.settingsPassword" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              <Trans i18nKey="docs.settingsDelete" components={{ strong: <strong /> }} />
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              <Trans i18nKey="docs.settingsSecurity" components={{ strong: <strong /> }} />
            </p>
          </section>

          {/* Security */}
          <section id="security" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.securityTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.securityP1')}
            </p>

            {/* Authenticator App */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px]">
              {t('docs.securityTotpTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.securityTotpP1')}
            </p>
            <Step n={1}><Trans i18nKey="docs.securityTotpStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.securityTotpStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.securityTotpStep3" components={{ strong: <strong /> }} /></Step>
            <Step n={4}><Trans i18nKey="docs.securityTotpStep4" components={{ strong: <strong /> }} /></Step>
            <Note>{t('docs.securityTotpNote')}</Note>

            {/* Illustration — Authenticator App enabled (light) */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)] dark:hidden">
              <div className="max-w-[380px] flex flex-col gap-[10px]">
                {/* Section title */}
                <span className="font-body text-[16px] font-semibold text-[#1D1D1E]">Two-Factor Authentication</span>
                {/* Authenticator card */}
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-[#1D1D1E]">Authenticator App</span>
                  <div className="flex items-center gap-[6px]">
                    <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[7px] py-[2px] rounded-[5px] bg-[#ECFDF5] text-[#059669]">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Active
                    </span>
                  </div>
                  <span className="font-body text-[13px] text-[#818185]">Your account is protected with an authenticator app.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] border border-[#D8210C] text-[#D8210C] font-body text-[12px] font-medium flex items-center">Disable</span>
                </div>
                {/* Divider */}
                <div className="border-t border-[rgba(0,0,0,0.07)]" />
                {/* Email card — not enabled */}
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-[#1D1D1E]">Email Verification</span>
                  <span className="font-body text-[13px] text-[#818185]">Receive a code to j••••e@example.com when you sign in.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium flex items-center">Enable</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.securityTotpCaption')}</p>
            </div>

            {/* Illustration — Authenticator App enabled (dark) */}
            <div className="hidden dark:block mb-[16px] p-[16px] rounded-[16px] bg-[#1C1C1F] border border-[rgba(255,255,255,0.06)]">
              <div className="max-w-[380px] flex flex-col gap-[10px]">
                <span className="font-body text-[16px] font-semibold text-white">Two-Factor Authentication</span>
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-white">Authenticator App</span>
                  <div className="flex items-center gap-[6px]">
                    <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[7px] py-[2px] rounded-[5px] bg-[#064E3B] text-[#34D399]">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Active
                    </span>
                  </div>
                  <span className="font-body text-[13px] text-[#E0E0E3]">Your account is protected with an authenticator app.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] border border-[#D8210C] text-[#D8210C] font-body text-[12px] font-medium flex items-center">Disable</span>
                </div>
                <div className="border-t border-[rgba(255,255,255,0.06)]" />
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-white">Email Verification</span>
                  <span className="font-body text-[13px] text-[#E0E0E3]">Receive a code to j••••e@example.com when you sign in.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] bg-[#C8C8CB] text-[#1C1C1F] font-body text-[12px] font-medium flex items-center">Enable</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#A1A1A6] italic mt-[10px]">{t('docs.securityTotpCaption')}</p>
            </div>

            {/* Email Verification */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[32px]">
              {t('docs.securityEmailTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.securityEmailP1')}
            </p>
            <Step n={1}><Trans i18nKey="docs.securityEmailStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.securityEmailStep2" components={{ strong: <strong /> }} /></Step>
            <Note>{t('docs.securityEmailNote')}</Note>

            {/* Illustration — Email Verification enabled (light) */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)] dark:hidden">
              <div className="max-w-[380px] flex flex-col gap-[10px]">
                <span className="font-body text-[16px] font-semibold text-[#1D1D1E]">Two-Factor Authentication</span>
                {/* Authenticator — not enabled */}
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-[#1D1D1E]">Authenticator App</span>
                  <span className="font-body text-[13px] text-[#818185]">Add an extra layer of security using an authenticator app.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium flex items-center">Enable</span>
                </div>
                <div className="border-t border-[rgba(0,0,0,0.07)]" />
                {/* Email — enabled */}
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-[#1D1D1E]">Email Verification</span>
                  <div className="flex items-center gap-[6px]">
                    <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[7px] py-[2px] rounded-[5px] bg-[#ECFDF5] text-[#059669]">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Active
                    </span>
                  </div>
                  <span className="font-body text-[13px] text-[#818185]">Codes are sent to j••••e@example.com when you sign in.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] border border-[#D8210C] text-[#D8210C] font-body text-[12px] font-medium flex items-center">Disable</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.securityEmailCaption')}</p>
            </div>

            {/* Illustration — Email Verification enabled (dark) */}
            <div className="hidden dark:block mb-[16px] p-[16px] rounded-[16px] bg-[#1C1C1F] border border-[rgba(255,255,255,0.06)]">
              <div className="max-w-[380px] flex flex-col gap-[10px]">
                <span className="font-body text-[16px] font-semibold text-white">Two-Factor Authentication</span>
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-white">Authenticator App</span>
                  <span className="font-body text-[13px] text-[#E0E0E3]">Add an extra layer of security using an authenticator app.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] bg-[#C8C8CB] text-[#1C1C1F] font-body text-[12px] font-medium flex items-center">Enable</span>
                </div>
                <div className="border-t border-[rgba(255,255,255,0.06)]" />
                <div className="flex flex-col gap-[6px]">
                  <span className="font-body text-[14px] font-semibold text-white">Email Verification</span>
                  <div className="flex items-center gap-[6px]">
                    <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[7px] py-[2px] rounded-[5px] bg-[#064E3B] text-[#34D399]">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Active
                    </span>
                  </div>
                  <span className="font-body text-[13px] text-[#E0E0E3]">Codes are sent to j••••e@example.com when you sign in.</span>
                  <span className="self-start h-[32px] px-[14px] rounded-[32px] border border-[#D8210C] text-[#D8210C] font-body text-[12px] font-medium flex items-center">Disable</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#A1A1A6] italic mt-[10px]">{t('docs.securityEmailCaption')}</p>
            </div>

            {/* Signing In with 2FA */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[32px]">
              {t('docs.securityLoginTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              {t('docs.securityLoginP1')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              {t('docs.securityLoginP2')}
            </p>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.securityLoginP3')}
            </p>

            {/* Illustration — MFA login step (light) */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)] dark:hidden">
              <div className="max-w-[300px] flex flex-col items-center gap-[12px]">
                <span className="font-display text-[18px] font-bold text-[#1D1D1E]">Two-Factor Authentication</span>
                <span className="font-body text-[13px] text-[#818185] text-center">Enter the 6-digit code from your authenticator app.</span>
                {/* 6-digit input mock */}
                <div className="flex gap-[6px]">
                  {[4, 2, 8, null, null, null].map((d, i) => (
                    <div key={i} className={`w-[36px] h-[44px] rounded-[10px] border flex items-center justify-center font-mono text-[18px] ${d != null ? 'border-[#1D1D1E] text-[#1D1D1E] bg-white' : 'border-[#BFBFC0] text-[#BFBFC0] bg-[#F9F9FA]'}`}>
                      {d != null ? d : '·'}
                    </div>
                  ))}
                </div>
                {/* Verify button */}
                <span className="w-full h-[40px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[14px] font-medium flex items-center justify-center">Verify</span>
                {/* Method links */}
                <div className="flex flex-col items-center gap-[6px]">
                  <span className="font-body text-[13px] font-medium text-[#1D1D1E]">Send a code to my email instead</span>
                  <span className="font-body text-[13px] text-[#818185]">Use a backup code</span>
                  <span className="font-body text-[13px] text-[#818185]">Back to login</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.securityLoginCaption')}</p>
            </div>

            {/* Illustration — MFA login step (dark) */}
            <div className="hidden dark:block mb-[16px] p-[16px] rounded-[16px] bg-[#1C1C1F] border border-[rgba(255,255,255,0.06)]">
              <div className="max-w-[300px] flex flex-col items-center gap-[12px]">
                <span className="font-display text-[18px] font-bold text-white">Two-Factor Authentication</span>
                <span className="font-body text-[13px] text-[#E0E0E3] text-center">Enter the 6-digit code from your authenticator app.</span>
                <div className="flex gap-[6px]">
                  {[4, 2, 8, null, null, null].map((d, i) => (
                    <div key={i} className={`w-[36px] h-[44px] rounded-[10px] border flex items-center justify-center font-mono text-[18px] ${d != null ? 'border-[#A0A0A3] text-white bg-[#28282B]' : 'border-[#3A3A3D] text-[#A1A1A6] bg-[#222225]'}`}>
                      {d != null ? d : '·'}
                    </div>
                  ))}
                </div>
                <span className="w-full h-[40px] rounded-[32px] bg-[#C8C8CB] text-[#1C1C1F] font-body text-[14px] font-medium flex items-center justify-center">Verify</span>
                <div className="flex flex-col items-center gap-[6px]">
                  <span className="font-body text-[13px] font-medium text-white">Send a code to my email instead</span>
                  <span className="font-body text-[13px] text-[#E0E0E3]">Use a backup code</span>
                  <span className="font-body text-[13px] text-[#E0E0E3]">Back to login</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#A1A1A6] italic mt-[10px]">{t('docs.securityLoginCaption')}</p>
            </div>

            {/* Push Notification MFA */}
            <h3 className="font-display text-[18px] font-bold text-base-black dark:text-white mb-[12px] mt-[32px]">
              {t('docs.securityPushTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] leading-relaxed mb-[16px]">
              {t('docs.securityPushP1')}
            </p>
            <div className="flex flex-col gap-[6px] mb-[16px]">
              <Step n={1}><Trans i18nKey="docs.securityPushStep1" components={{ strong: <strong /> }} /></Step>
              <Step n={2}><Trans i18nKey="docs.securityPushStep2" components={{ strong: <strong /> }} /></Step>
              <Step n={3}><Trans i18nKey="docs.securityPushStep3" components={{ strong: <strong /> }} /></Step>
              <Step n={4}><Trans i18nKey="docs.securityPushStep4" components={{ strong: <strong /> }} /></Step>
            </div>
            <Note><Trans i18nKey="docs.securityPushNote" components={{ strong: <strong /> }} /></Note>

            {/* Illustration — Push Notifications (light) */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)] dark:hidden">
              <div className="max-w-[380px] flex flex-col gap-[12px]">
                <span className="font-body text-[16px] font-semibold text-[#1D1D1E]">Push Notifications</span>
                <div className="flex items-center gap-[8px] mb-[-4px]">
                  <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#ECFDF5] text-[#059669]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    Active
                  </span>
                </div>
                <span className="font-body text-[13px] text-[#818185]">You'll receive a push notification to approve sign-in requests.</span>
                <span className="self-start h-[32px] px-[14px] rounded-[32px] border border-[#D8210C] text-[#D8210C] font-body text-[12px] font-medium flex items-center">Disable</span>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.securityPushCaption')}</p>
            </div>

            {/* Illustration — Push Notifications (dark) */}
            <div className="hidden dark:block mb-[16px] p-[16px] rounded-[16px] bg-[#1C1C1F] border border-[rgba(255,255,255,0.06)]">
              <div className="max-w-[380px] flex flex-col gap-[12px]">
                <span className="font-body text-[16px] font-semibold text-white">Push Notifications</span>
                <div className="flex items-center gap-[8px] mb-[-4px]">
                  <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#064E3B] text-[#34D399]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    Active
                  </span>
                </div>
                <span className="font-body text-[13px] text-[#E0E0E3]">You'll receive a push notification to approve sign-in requests.</span>
                <span className="self-start h-[32px] px-[14px] rounded-[32px] border border-[#D8210C] text-[#D8210C] font-body text-[12px] font-medium flex items-center">Disable</span>
              </div>
              <p className="font-body text-[11px] text-[#A1A1A6] italic mt-[10px]">{t('docs.securityPushCaption')}</p>
            </div>

            {/* iOS Setup */}
            <h4 className="font-display text-[16px] font-bold text-base-black dark:text-white mb-[8px] mt-[24px]">
              {t('docs.securityPushIosTitle')}
            </h4>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] leading-relaxed mb-[16px]">
              {t('docs.securityPushIosP1')}
            </p>
            <div className="flex flex-col gap-[6px] mb-[16px]">
              <Step n={1}><Trans i18nKey="docs.securityPushIosStep1" components={{ strong: <strong /> }} /></Step>
              <Step n={2}><Trans i18nKey="docs.securityPushIosStep2" components={{ strong: <strong /> }} /></Step>
              <Step n={3}><Trans i18nKey="docs.securityPushIosStep3" components={{ strong: <strong /> }} /></Step>
              <Step n={4}><Trans i18nKey="docs.securityPushIosStep4" components={{ strong: <strong /> }} /></Step>
              <Step n={5}><Trans i18nKey="docs.securityPushIosStep5" components={{ strong: <strong /> }} /></Step>
            </div>
            <Note><Trans i18nKey="docs.securityPushIosNote" components={{ strong: <strong /> }} /></Note>

            {/* Illustration — iOS Add to Home Screen (light) */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)] dark:hidden">
              <div className="max-w-[380px] flex flex-col items-center gap-[12px]">
                <div className="flex items-center gap-[12px]">
                  <div className="w-[48px] h-[48px] rounded-[12px] bg-white border border-[#E5E5ED] shadow-[0_1px_3px_rgba(0,0,0,0.07)] flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <circle cx="8" cy="12" r="2" fill="#0ea5c9" stroke="none" />
                      <circle cx="12" cy="12" r="2" fill="#1D1D1E" stroke="none" />
                      <circle cx="16" cy="12" r="2" fill="#1D1D1E" opacity="0.3" stroke="none" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-body text-[14px] font-semibold text-[#1D1D1E] block">Standby</span>
                    <span className="font-body text-[11px] text-[#818185]">standbyqueue.com</span>
                  </div>
                </div>
                <div className="w-full flex items-center gap-[8px] px-[12px] py-[10px] rounded-[12px] bg-white border border-[#E5E5ED]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#818185" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  <span className="font-body text-[13px] text-[#1D1D1E]">Add to Home Screen</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">iOS: Tap Share then "Add to Home Screen" to enable push notifications.</p>
            </div>

            {/* Illustration — iOS Add to Home Screen (dark) */}
            <div className="hidden dark:block mb-[16px] p-[16px] rounded-[16px] bg-[#1C1C1F] border border-[rgba(255,255,255,0.06)]">
              <div className="max-w-[380px] flex flex-col items-center gap-[12px]">
                <div className="flex items-center gap-[12px]">
                  <div className="w-[48px] h-[48px] rounded-[12px] bg-[#28282B] border border-[#3A3A3D] shadow-[0_1px_3px_rgba(0,0,0,0.3)] flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <circle cx="8" cy="12" r="2" fill="#0ea5c9" stroke="none" />
                      <circle cx="12" cy="12" r="2" fill="white" stroke="none" />
                      <circle cx="16" cy="12" r="2" fill="white" opacity="0.3" stroke="none" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-body text-[14px] font-semibold text-white block">Standby</span>
                    <span className="font-body text-[11px] text-[#A1A1A6]">standbyqueue.com</span>
                  </div>
                </div>
                <div className="w-full flex items-center gap-[8px] px-[12px] py-[10px] rounded-[12px] bg-[#28282B] border border-[#3A3A3D]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A1A1A6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  <span className="font-body text-[13px] text-white">Add to Home Screen</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#A1A1A6] italic mt-[10px]">iOS: Tap Share then "Add to Home Screen" to enable push notifications.</p>
            </div>

            <Tip>{t('docs.securityBothMethodsTip')}</Tip>

            {/* Active Sessions */}
            <h3 className="font-display text-[18px] font-bold text-base-black dark:text-white mb-[12px] mt-[32px]">
              {t('docs.securitySessionsTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.securitySessionsP1" components={{ strong: <strong /> }} />
            </p>

            {/* Illustration — Active Sessions (light) */}
            <div className="mb-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)] dark:hidden">
              <div className="max-w-[380px] flex flex-col gap-[8px]">
                <span className="font-body text-[16px] font-semibold text-[#1D1D1E]">Active Sessions</span>
                <span className="font-body text-[13px] text-[#818185]">Log out of all other devices and sessions except this one.</span>
                <span className="self-start h-[40px] px-[20px] rounded-[32px] border border-[#E5E5ED] bg-[#EFEFF1] font-body text-[13px] font-medium text-[#1D1D1E] flex items-center">Log Out All Devices</span>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.securitySessionsCaption')}</p>
            </div>

            {/* Illustration — Active Sessions (dark) */}
            <div className="hidden dark:block mb-[16px] p-[16px] rounded-[16px] bg-[#1C1C1F] border border-[rgba(255,255,255,0.06)]">
              <div className="max-w-[380px] flex flex-col gap-[8px]">
                <span className="font-body text-[16px] font-semibold text-white">Active Sessions</span>
                <span className="font-body text-[13px] text-[#E0E0E3]">Log out of all other devices and sessions except this one.</span>
                <span className="self-start h-[40px] px-[20px] rounded-[32px] border border-[#3A3A3D] bg-[#28282B] font-body text-[13px] font-medium text-white flex items-center">Log Out All Devices</span>
              </div>
              <p className="font-body text-[11px] text-[#A1A1A6] italic mt-[10px]">{t('docs.securitySessionsCaption')}</p>
            </div>
          </section>

          {/* Webhook Notifications */}
          <section id="webhook-notifications" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.webhookNotificationsTitle')}<ProBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.webhookNotificationsP1" components={{ strong: <strong /> }} />
            </p>

            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[8px]">
              {t('docs.webhookNotificationsEvents')}
            </p>
            <ul className="list-disc ml-[20px] mb-[16px] font-body text-[14px] text-[#374151] leading-relaxed flex flex-col gap-[4px]">
              <li><Trans i18nKey="docs.webhookEventJoined" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.webhookEventActivated" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.webhookEventDone" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.webhookEventMeeting" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="docs.webhookEventEnded" components={{ strong: <strong /> }} /></li>
            </ul>

            <Note>
              <Trans i18nKey="docs.webhookWhereP1" components={{ strong: <strong /> }} />
            </Note>

            {/* Discord */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[32px] flex items-center gap-[8px]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" /></svg>
              {t('docs.discordSetupTitle')}
            </h3>

            <h4 className="font-display text-[15px] font-bold text-base-black mb-[8px]">
              {t('docs.discordPermissionsTitle')}
            </h4>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              <Trans i18nKey="docs.discordPermissionsP1" components={{ strong: <strong /> }} />
            </p>

            <h4 className="font-display text-[15px] font-bold text-base-black mb-[12px]">
              {t('docs.discordSetupStepsTitle')}
            </h4>
            <Step n={1}><Trans i18nKey="docs.discordStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.discordStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.discordStep3" components={{ strong: <strong /> }} /></Step>
            <Tip><Trans i18nKey="docs.discordTip" components={{ code: <Code /> }} /></Tip>

            {/* Slack */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[32px] flex items-center gap-[8px]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#E01E5A"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" /></svg>
              {t('docs.slackSetupTitle')}
            </h3>

            <h4 className="font-display text-[15px] font-bold text-base-black mb-[8px]">
              {t('docs.slackPermissionsTitle')}
            </h4>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.slackPermissionsP1')}
            </p>

            <h4 className="font-display text-[15px] font-bold text-base-black mb-[12px]">
              {t('docs.slackSetupStepsTitle')}
            </h4>
            <Step n={1}><Trans i18nKey="docs.slackStep1" components={{ strong: <strong /> }} /></Step>
            <Step n={2}><Trans i18nKey="docs.slackStep2" components={{ strong: <strong /> }} /></Step>
            <Step n={3}><Trans i18nKey="docs.slackStep3" components={{ strong: <strong /> }} /></Step>
            <Tip><Trans i18nKey="docs.slackTip" components={{ code: <Code /> }} /></Tip>

            {/* Indicator pill note */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[12px] mt-[32px]">
              {t('docs.webhookIndicatorTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed">
              <Trans i18nKey="docs.webhookIndicatorP1" components={{ strong: <strong /> }} />
            </p>
            {/* Illustration — Webhook indicator pills on review card */}
            <div className="mt-[16px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px]">
                <div className="flex flex-col gap-[4px] p-[12px_16px] rounded-[12px] border border-[rgba(0,0,0,0.11)] bg-[#F5F5F6]">
                  <p className="font-body text-[15px] font-semibold text-[#111827] truncate">Sprint Review</p>
                  <div className="flex items-center gap-[6px] flex-wrap">
                    <span className="font-body text-[12px] text-gray-900">3 participants</span>
                    <span className="inline-flex items-center justify-center w-[24px] h-[24px] rounded-full flex-shrink-0" style={{ color: '#5865F2', backgroundColor: '#5865F21A' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" /></svg>
                    </span>
                    <span className="inline-flex items-center justify-center w-[24px] h-[24px] rounded-full flex-shrink-0" style={{ color: '#E01E5A', backgroundColor: '#E01E5A1A' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" /></svg>
                    </span>
                    <span className="inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[32px] text-[11px] font-semibold bg-green-500 text-green-900 border border-green-900/30">
                      <span className="w-[6px] h-[6px] rounded-full bg-green-900" />In Progress
                    </span>
                    <span className="font-mono text-[12px] text-gray-700">/sprint-review</span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.webhookIndicatorCaption')}</p>
            </div>
          </section>

          {/* Saved Webhooks Library */}
          <section id="saved-webhooks" className="mb-[48px] scroll-mt-[230px] md:scroll-mt-[210px]">
            <h2 className="font-display text-[24px] font-bold text-base-black tracking-[-0.4px] mb-[16px]">
              {t('docs.savedWebhooksTitle')}<ProBadge />
            </h2>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[24px]">
              {t('docs.savedWebhooksP1')}
            </p>

            {/* Illustration A — Settings list */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[10px]">
              {t('docs.savedWebhooksSettingsTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.savedWebhooksSettingsP1')}
            </p>
            <div className="mb-[24px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="flex flex-col gap-[6px] mb-[10px]">
                {/* Saved webhook row — Discord */}
                <div className="flex items-center gap-[10px] p-[10px_14px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.09)]">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#5865F2" className="flex-shrink-0"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" /></svg>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-[13px] font-semibold text-base-black">Sprint reviews</p>
                    <p className="font-mono text-[11px] text-[#818185]">discord.com/api/webhooks/••••</p>
                  </div>
                  <div className="w-[28px] h-[28px] rounded-full bg-[#F5F5F6] flex items-center justify-center text-[#BFBFC0]">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
                  </div>
                </div>
                {/* Saved webhook row — Slack */}
                <div className="flex items-center gap-[10px] p-[10px_14px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.09)]">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#E01E5A" className="flex-shrink-0"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" /></svg>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-[13px] font-semibold text-base-black">Design team</p>
                    <p className="font-mono text-[11px] text-[#818185]">hooks.slack.com/services/••••</p>
                  </div>
                  <div className="w-[28px] h-[28px] rounded-full bg-[#F5F5F6] flex items-center justify-center text-[#BFBFC0]">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
                  </div>
                </div>
                {/* Add button row */}
                <div className="flex items-center gap-[10px] p-[10px_14px] rounded-[12px] border border-dashed border-[#BFBFC0]">
                  <span className="font-body text-[13px] text-[#818185]">+ {t('docs.savedWebhooksAddLabel')}</span>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic">{t('docs.savedWebhooksSettingsCaption')}</p>
            </div>

            {/* Slack Channel Link */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[10px]">
              {t('docs.savedWebhooksSlackLinkTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.savedWebhooksSlackLinkP1')}
            </p>

            {/* Illustration — Slack Channel Link form + pill preview */}
            <div className="mb-[24px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px] flex flex-col gap-[10px]">
                {/* Panel header */}
                <div className="flex items-center gap-[8px]">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#E01E5A" className="flex-shrink-0"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" /></svg>
                  <span className="font-body text-[14px] font-semibold text-base-black">Slack Notifications</span>
                  <span className="inline-flex items-center gap-[4px] px-[8px] py-[2px] rounded-[5px] bg-[#dcfce7] text-[#15803d] text-[11px] font-semibold">
                    <span className="w-[5px] h-[5px] rounded-full bg-[#15803d]" />
                    {t('review.webhookConnected')}
                  </span>
                </div>
                {/* Edit form */}
                <div className="flex flex-col gap-[6px]">
                  {/* Webhook URL */}
                  <div className="h-[36px] px-[12px] flex items-center bg-white border border-[#BFBFC0] rounded-[18px] overflow-hidden">
                    <span className="font-mono text-[12px] text-base-black truncate">https://hooks.slack.com/services/T03B8••••</span>
                  </div>
                  {/* Channel URL — highlighted as the "new" field */}
                  <div className="flex flex-col gap-[3px]">
                    <div className="h-[36px] px-[12px] flex items-center bg-white border border-[#0ea5c9] rounded-[18px] shadow-[0_0_0_3px_rgba(14,165,201,0.12)] overflow-hidden">
                      <span className="font-mono text-[12px] text-base-black truncate">https://app.slack.com/client/T03B8••••/C04••••</span>
                    </div>
                    <span className="font-body text-[11px] text-[#818185] pl-[4px]">{t('docs.slackChannelUrlFieldHint')}</span>
                  </div>
                  {/* Buttons */}
                  <div className="flex items-center gap-[6px] mt-[2px]">
                    <span className="h-[32px] px-[14px] rounded-[18px] text-white font-body text-[12px] font-medium flex items-center" style={{ backgroundColor: '#E01E5A' }}>{t('common.save')}</span>
                    <span className="h-[32px] px-[14px] rounded-[18px] border border-[#E5E5ED] bg-[#EFEFF1] font-body text-[12px] font-medium text-base-black flex items-center">{t('common.cancel')}</span>
                  </div>
                </div>
                {/* Divider + pill preview */}
                <div className="pt-[8px] border-t border-[rgba(0,0,0,0.07)]">
                  <p className="font-body text-[11px] text-[#818185] mb-[6px]">{t('docs.slackPillResultLabel')}</p>
                  <div className="flex items-center gap-[10px]">
                    {/* Non-clickable (no channel URL) */}
                    <div className="flex flex-col items-center gap-[3px]">
                      <span className="inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-[5px] text-[12px] font-semibold" style={{ color: '#E01E5A', backgroundColor: '#E01E5A1A' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" /></svg>
                        Slack
                      </span>
                      <span className="font-body text-[10px] text-[#BFBFC0]">{t('docs.slackPillWithout')}</span>
                    </div>
                    <span className="text-[#BFBFC0] text-[14px]">→</span>
                    {/* Clickable (with channel URL) */}
                    <div className="flex flex-col items-center gap-[3px]">
                      <span className="inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-[5px] text-[12px] font-semibold underline decoration-dotted" style={{ color: '#E01E5A', backgroundColor: '#E01E5A1A' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" /></svg>
                        Slack
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                      </span>
                      <span className="font-body text-[10px] text-[#0ea5c9]">{t('docs.slackPillWith')}</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.savedWebhooksSlackLinkCaption')}</p>
            </div>

            {/* Illustration B — Autocomplete */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[10px]">
              {t('docs.savedWebhooksAutocompleteTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.savedWebhooksAutocompleteP1')}
            </p>
            <div className="mb-[24px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="relative max-w-[360px]">
                {/* Focused input */}
                <div className="h-[36px] px-[12px] flex items-center bg-white border border-[#BFBFC0] rounded-[18px] shadow-[0_0_0_4px_rgba(29,29,30,0.08)] text-[13px] font-body text-base-black gap-[6px]">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" /></svg>
                  <span className="text-[#BFBFC0]">Sprint</span>
                  <span className="w-[1.5px] h-[14px] bg-base-black animate-pulse inline-block" />
                </div>
                {/* Dropdown */}
                <div className="mt-[4px] bg-white rounded-[12px] border border-[rgba(0,0,0,0.11)] shadow-[0_4px_20px_rgba(0,0,0,0.10)] py-[4px]">
                  <div className="flex items-center gap-[8px] px-[12px] py-[8px] bg-[#EFEFF1] rounded-[8px] mx-[4px]">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" /></svg>
                    <span className="font-body text-[13px] font-semibold text-base-black">Sprint reviews</span>
                    <span className="font-mono text-[11px] text-[#818185] ml-auto">discord.com/••••</span>
                  </div>
                  <div className="flex items-center gap-[8px] px-[12px] py-[8px]">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" /></svg>
                    <span className="font-body text-[13px] text-base-black">All-hands</span>
                    <span className="font-mono text-[11px] text-[#818185] ml-auto">discord.com/••••</span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.savedWebhooksAutocompleteCaption')}</p>
            </div>

            {/* Illustration C — Save prompt */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[10px]">
              {t('docs.savedWebhooksSavePromptTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.savedWebhooksSavePromptP1')}
            </p>
            <div className="mb-[24px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px] flex flex-col gap-[8px]">
                {/* Connected state */}
                <div className="flex items-center gap-[8px] flex-wrap">
                  <span className="font-mono text-[12px] text-[#818185]">discord.com/api/webhooks/••••</span>
                  <span className="inline-flex items-center gap-[4px] px-[8px] py-[2px] rounded-[5px] bg-[#dcfce7] text-[#15803d] text-[11px] font-semibold">
                    <span className="w-[5px] h-[5px] rounded-full bg-[#15803d]" />
                    {t('docs.savedWebhooksConnectedLabel')}
                  </span>
                </div>
                {/* Save prompt */}
                <div className="p-[12px_14px] rounded-[9px] bg-white border border-[rgba(0,0,0,0.09)] border-l-4 border-l-[#0ea5c9]">
                  <p className="font-body text-[13px] font-semibold text-base-black mb-[8px]">{t('docs.savedWebhooksSavePromptLabel')}</p>
                  <div className="flex items-center gap-[6px] flex-wrap">
                    <div className="flex-1 min-w-[120px] h-[30px] px-[10px] rounded-[15px] border border-[#BFBFC0] bg-[#F9F9FA] flex items-center">
                      <span className="font-body text-[12px] text-[#BFBFC0]">{t('docs.savedWebhooksNameHint')}</span>
                    </div>
                    <span className="h-[30px] px-[10px] rounded-[9px] bg-[#0ea5c9] text-white font-body text-[12px] font-medium flex items-center">{t('common.save')}</span>
                    <span className="font-body text-[12px] text-[#818185]">{t('common.dismiss')}</span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.savedWebhooksSavePromptCaption')}</p>
            </div>

            {/* Editing */}
            <h3 className="font-display text-[18px] font-bold text-base-black mb-[10px]">
              {t('docs.savedWebhooksEditTitle')}
            </h3>
            <p className="font-body text-[14px] text-[#374151] leading-relaxed mb-[16px]">
              {t('docs.savedWebhooksEditP1')}
            </p>

            {/* Illustration — Editing Saved Webhooks */}
            <div className="mb-[24px] p-[16px] rounded-[16px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]">
              <div className="max-w-[380px] flex flex-col gap-[8px]">
                {/* Collapsed Discord card */}
                <div className="flex items-center gap-[10px] p-[10px_14px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.08)]">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#5865F2" className="flex-shrink-0"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" /></svg>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-[13px] font-semibold text-base-black">Sprint reviews</p>
                    <p className="font-mono text-[11px] text-[#818185]">discord.com/api/webhooks/••••</p>
                  </div>
                  <div className="flex items-center gap-[6px]">
                    <div className="w-[26px] h-[26px] rounded-full bg-[#F5F5F6] flex items-center justify-center text-[#818185]">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </div>
                    <div className="w-[26px] h-[26px] rounded-full bg-[#F5F5F6] flex items-center justify-center text-[#BFBFC0]">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
                    </div>
                  </div>
                </div>
                {/* Expanded Slack card — edit mode */}
                <div className="flex flex-col gap-[8px] p-[12px_14px] rounded-[12px] bg-white border border-[#0ea5c9] shadow-[0_0_0_3px_rgba(14,165,201,0.08)]">
                  <div className="flex items-center gap-[8px]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#E01E5A" className="flex-shrink-0"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" /></svg>
                    <span className="font-body text-[12px] font-semibold text-base-black">Editing</span>
                  </div>
                  {/* Name input */}
                  <div className="h-[32px] px-[12px] flex items-center bg-[#F9F9FA] border border-[#BFBFC0] rounded-[18px]">
                    <span className="font-body text-[12px] text-base-black">Design team</span>
                  </div>
                  {/* URL input */}
                  <div className="h-[32px] px-[12px] flex items-center bg-[#F9F9FA] border border-[#BFBFC0] rounded-[18px] overflow-hidden">
                    <span className="font-mono text-[11px] text-base-black truncate">https://hooks.slack.com/services/T03B8••••</span>
                  </div>
                  {/* Channel URL input */}
                  <div className="flex flex-col gap-[3px]">
                    <div className="h-[32px] px-[12px] flex items-center bg-white border border-[#0ea5c9] rounded-[18px] shadow-[0_0_0_3px_rgba(14,165,201,0.10)] overflow-hidden">
                      <span className="font-mono text-[11px] text-base-black truncate">https://app.slack.com/client/T03B8••••/C04••••</span>
                    </div>
                    <span className="font-body text-[10px] text-[#818185] pl-[4px]">{t('docs.slackChannelUrlFieldHint')}</span>
                  </div>
                  {/* Save / Cancel */}
                  <div className="flex items-center gap-[6px]">
                    <span className="h-[28px] px-[12px] rounded-[14px] text-white font-body text-[11px] font-medium flex items-center" style={{ backgroundColor: '#E01E5A' }}>{t('common.save')}</span>
                    <span className="h-[28px] px-[12px] rounded-[14px] border border-[#E5E5ED] bg-[#EFEFF1] font-body text-[11px] font-medium text-base-black flex items-center">{t('common.cancel')}</span>
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-[#818185] italic mt-[10px]">{t('docs.savedWebhooksEditCaption')}</p>
            </div>

            <Note>
              <Trans i18nKey="docs.savedWebhooksNote" components={{ strong: <strong /> }} />
            </Note>
          </section>
        </article>
      </div>
    </div>
  );
};
